import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import axios from 'axios';
import config from '../config';
import useRingtone from '../hooks/useRingtone';
import '../styles/AudioCall.css';
// Reuse the modal/contacts styles defined for VideoCall (Add Participants UI).
import '../styles/VideoCall.css';
import EndCallIcon from './icons/EndCallIcon';
import MuteIcon from './icons/MuteIcon';
import { leaveOtherCallSessions, registerCallSession } from '../utils/groupCallSession';
import {
  CALL_RECONNECT_GRACE_MS,
  clearActiveCall,
  consumeActiveCall,
  isPageUnloading,
  readActiveCall,
  saveActiveCall,
  waitForSocket,
} from '../utils/activeCallPersistence';

const MAX_CALL_PARTICIPANTS = 5;

const generateCallId = () => {
  if (typeof window !== 'undefined' && window.crypto?.randomUUID) {
    return window.crypto.randomUUID();
  }
  return `call-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
};

const AudioCall = ({ user, selectedUser, onEndCall, socket }) => {
  const [isCallActive, setIsCallActive] = useState(false);
  const [isIncomingCall, setIsIncomingCall] = useState(false);
  const [callerId, setCallerId] = useState(null);
  const [callerName, setCallerName] = useState(null);
  const [remoteName, setRemoteName] = useState(null);
  const [localStream, setLocalStream] = useState(null);
  const [remoteStream, setRemoteStream] = useState(null);
  const [peerConnection, setPeerConnection] = useState(null);
  // Ref-mirror so hold/swap logic and ICE handlers can work without waiting on re-renders.
  const peerConnectionRef = useRef(null);
  const setPeerConnectionSafe = (pc) => { peerConnectionRef.current = pc; setPeerConnection(pc); };
  const [callStatus, _setCallStatus] = useState('idle'); // idle | calling | incoming | connected | ended
  // Ref-mirror so socket handlers (with stale closures) can read the live status.
  const callStatusRef = useRef('idle');
  const sessionIdRef = useRef(`direct-audio-${Math.random().toString(36).slice(2, 10)}`);
  const allParticipantsRef = useRef(new Set());
  const sendingResumeOffersRef = useRef(false);
  const resumeAttemptedRef = useRef(false);
  const hadLiveCallRef = useRef(false);
  const buildAudioSnapshotRef = useRef(() => null);
  const reconnectTimerRef = useRef(null);
  const reconnectAttemptsRef = useRef(0);
  const endCallRef = useRef(() => {});
  const setCallStatus = (s) => { callStatusRef.current = s; _setCallStatus(s); };
  const [isMuted, setIsMuted] = useState(false);
  const [callDuration, setCallDuration] = useState(0);
  const [error, setError] = useState(null);
  // "Call waiting": notification when another call arrives while we are already in a call.
  const [waitingCall, setWaitingCall] = useState(null);
  const waitingCallRef = useRef(null);
  const setWaitingCallSafe = (v) => { waitingCallRef.current = v; setWaitingCall(v); };
  // Held call (telephone-style): when we accept a second call mid-call, the original is put
  // on hold (PC kept alive, outgoing audio paused via replaceTrack(null), peer notified).
  const heldCallRef = useRef(null);
  const [hasHeldCall, setHasHeldCall] = useState(false);
  const [heldCallName, setHeldCallName] = useState('');
  const [isHeldByPeer, setIsHeldByPeer] = useState(false);

  // Multi-participant (mesh conference) state — same shape as VideoCall.js, but audio-only.
  const [showAddParticipants, setShowAddParticipants] = useState(false);
  const [contacts, setContacts] = useState([]);
  const [selectedContacts, setSelectedContacts] = useState(new Set());
  const [participantSearchQuery, setParticipantSearchQuery] = useState('');
  const [allParticipants, setAllParticipants] = useState(new Set());
  const [remoteParticipantStreams, setRemoteParticipantStreams] = useState([]); // [{userId, stream}]
  const [participantNames, setParticipantNames] = useState({});
  const additionalPeerConnectionsRef = useRef(new Map()); // Map<userId, RTCPeerConnection>
  const remoteStreamsRef = useRef(new Map()); // Map<userId, MediaStream>
  // ICE candidates can arrive before remoteDescription is set; queue per peer to avoid drops.
  const pendingMultiIceCandidatesRef = useRef(new Map()); // Map<userId, RTCIceCandidateInit[]>
  const isMultiParticipantCallRef = useRef(false);
  // Each remote audio stream needs its own hidden <audio> element to play out loud.
  const multiRemoteAudioElsRef = useRef(new Map()); // Map<userId, HTMLAudioElement>

  const localAudioRef = useRef(null);
  const remoteAudioRef = useRef(null);
  const peerIdRef = useRef(null);
  const durationTimerRef = useRef(null);
  // ---- Call-history bookkeeping (additive; does not affect call flow) ----
  // direction of the current call relative to this user, whether it ever
  // connected, when it connected, and why it ended. Used only to build the
  // call-log entry forwarded via onEndCall.
  const callDirectionRef = useRef(null); // 'outgoing' | 'incoming'
  const callConnectedRef = useRef(false);
  const callConnectedAtRef = useRef(null);
  const callEndReasonRef = useRef(null); // 'rejected' when peer/we explicitly rejected
  const pendingIceCandidates = useRef([]);
  const pendingOffer = useRef(null);
  // Most-complete `existingParticipants` we've learned about an incoming
  // multi-party audio invite — keyed by inviter id. We union the lists from
  // both `multiAudioCallOffer` (server-relayed, captured at offer-relay time)
  // and `incomingMultiAudioCall` (server-relayed after folding in all
  // existingParticipants from the inviter) so the new joiner can mesh with
  // every actual participant on accept, not just the inviter + whoever
  // happened to be in the call when their offer was relayed.
  const multiCallInviteParticipantsRef = useRef(new Map()); // Map<fromId:string, Set<participantId>>
  const autoAcceptCall = useRef(false);
  const callIdRef = useRef(null);
  const localStreamRef = useRef(null);
  const remoteStreamRef = useRef(null);
  const cleanupDoneRef = useRef(false);

  // The user we are currently calling. Normally identical to `selectedUser`,
  // but when this component is mounted globally (App.js) without a
  // `selectedUser` prop, the chat-header button dispatches a
  // `requestStartAudioCall` event with the target user; we then store that
  // user here so endCall / call-history bookkeeping still works.
  const callTargetRef = useRef(null);

  // Tracks calls (by peer + callId) that we've just disconnected from. We use
  // this to drop *stale* incoming-call signals that may arrive in the socket
  // buffer (or via the OS push notification's click handler) right after we
  // hung up, otherwise they re-trigger the "Incoming Call" UI for a call the
  // user already ended. Entries are ignored after RECENT_END_GUARD_MS.
  const recentlyEndedCallsRef = useRef(new Map());
  const RECENT_END_GUARD_MS = 4000;

  const markRecentlyEnded = (peerId, callId) => {
    const now = Date.now();
    if (peerId) recentlyEndedCallsRef.current.set(peerId.toString(), now);
    if (callId) recentlyEndedCallsRef.current.set(`call:${callId}`, now);
    // Garbage-collect old entries.
    for (const [key, ts] of recentlyEndedCallsRef.current.entries()) {
      if (now - ts > RECENT_END_GUARD_MS * 4) {
        recentlyEndedCallsRef.current.delete(key);
      }
    }
  };

  const isRecentlyEnded = ({ from, callId } = {}) => {
    const now = Date.now();
    const map = recentlyEndedCallsRef.current;
    // If a callId is present we can be precise: only match when the callId is
    // the same one we just hung up on. A *different* callId from the same peer
    // is a brand-new call and must be allowed through.
    if (callId) {
      const ts = map.get(`call:${callId}`);
      return !!(ts && now - ts <= RECENT_END_GUARD_MS);
    }
    // No callId on the event (legacy / video offer paths). Fall back to a
    // tighter peer-based window so genuine re-calls aren't suppressed for long.
    if (from) {
      const ts = map.get(from.toString());
      if (ts && now - ts <= 2000) return true;
    }
    return false;
  };

  // Ask the service worker to close any lingering OS-level call notifications
  // for the given peer so a click on a stale notification can't re-open the
  // incoming-call UI after we've already hung up.
  const clearCallNotificationsInSW = (peerId) => {
    try {
      if ('serviceWorker' in navigator && navigator.serviceWorker.controller) {
        navigator.serviceWorker.controller.postMessage({
          type: 'CLEAR_CALL_NOTIFICATIONS',
          data: { from: peerId ? peerId.toString() : undefined, callType: 'audio' },
        });
      }
    } catch (_) { /* best effort */ }
  };

  // Ringtone hook for incoming calls
  const { playRingtone, stopRingtone, resetCallAccepted } = useRingtone();

  // Resolve a user name by id
  const fetchUserName = async (id) => {
    try {
      const res = await axios.get(`${config.apiUrl}/api/auth/profile/${id}?requestingUserId=${user.id}`);
      return res.data?.username || id;
    } catch (e) {
      return id;
    }
  };

  // ---- Multi-participant helpers (mesh conference for audio) ----
  const queueMultiIceCandidate = (userId, candidate) => {
    if (!userId || !candidate) return;
    const uid = userId.toString();
    const existing = pendingMultiIceCandidatesRef.current.get(uid) || [];
    existing.push(candidate);
    pendingMultiIceCandidatesRef.current.set(uid, existing);
  };

  const flushMultiIceCandidates = async (userId, pc) => {
    const uid = userId?.toString();
    if (!uid || !pc) return;
    const queued = pendingMultiIceCandidatesRef.current.get(uid);
    if (!queued || queued.length === 0) return;
    if (!pc.remoteDescription) return;
    pendingMultiIceCandidatesRef.current.delete(uid);
    for (const c of queued) {
      try {
        await pc.addIceCandidate(new RTCIceCandidate(c));
      } catch (err) {
        console.error('AudioCall: error adding queued ICE candidate for', uid, ':', err);
      }
    }
  };

  const syncRemoteParticipants = () => {
    const entries = Array.from(remoteStreamsRef.current.entries())
      .filter(([uid, stream]) => uid && stream && typeof stream.getTracks === 'function' && stream.getTracks().length > 0)
      .map(([uid, stream]) => ({ userId: uid.toString(), stream }));
    entries.sort((a, b) => a.userId.localeCompare(b.userId));
    setRemoteParticipantStreams(entries);
  };

  const ensureRemoteAudioElement = (userId, stream) => {
    if (!userId || !stream) return null;
    const uid = userId.toString();
    let el = multiRemoteAudioElsRef.current.get(uid);
    if (!el) {
      el = document.createElement('audio');
      el.autoplay = true;
      el.playsInline = true;
      el.muted = false;
      el.volume = 1.0;
      el.style.display = 'none';
      document.body.appendChild(el);
      multiRemoteAudioElsRef.current.set(uid, el);
    }
    if (el.srcObject !== stream) {
      el.srcObject = stream;
    }
    el.play().catch(() => {
      setTimeout(() => {
        try { el.play().catch(() => {}); } catch (_) { /* noop */ }
      }, 300);
    });
    return el;
  };

  const removeRemoteAudioElement = (userId) => {
    const uid = userId?.toString();
    if (!uid) return;
    const el = multiRemoteAudioElsRef.current.get(uid);
    if (el) {
      try { el.pause(); } catch (_) { /* noop */ }
      try { el.srcObject = null; } catch (_) { /* noop */ }
      try { el.remove(); } catch (_) { /* noop */ }
      multiRemoteAudioElsRef.current.delete(uid);
    }
  };

  const upsertRemoteStream = (userId, stream) => {
    if (!userId || !stream) return;
    const uid = userId.toString();
    remoteStreamsRef.current.set(uid, stream);
    ensureRemoteAudioElement(uid, stream);
    syncRemoteParticipants();
  };

  // Merge a freshly-received `existingParticipants` list (from either
  // `multiAudioCallOffer` or `incomingMultiAudioCall`) into the running
  // snapshot for `fromId`, and keep the pendingOffer in sync. The new joiner
  // needs the union of both events so mesh links can be established with
  // every actual participant on accept (the offer event captures the list at
  // the time the offer was relayed, the invite event captures it after the
  // server has folded in all existingParticipants from the inviter).
  const recordInviteParticipants = (fromId, participants) => {
    if (!fromId) return;
    const key = fromId.toString();
    const set = multiCallInviteParticipantsRef.current.get(key) || new Set();
    if (Array.isArray(participants)) {
      participants.forEach((p) => {
        const s = p?.toString();
        if (s) set.add(s);
      });
    }
    multiCallInviteParticipantsRef.current.set(key, set);
    if (pendingOffer.current && pendingOffer.current.from?.toString() === key) {
      pendingOffer.current.existingParticipants = Array.from(set);
    }
  };

  // Listen for service worker messages (audio call accept/reject from notification)
  useEffect(() => {
    if (!('serviceWorker' in navigator)) return;

    const handleServiceWorkerMessage = (event) => {
      const { type, data } = event.data || {};
      
      // Handle ringtone control from service worker
      if (type === 'PLAY_RINGTONE') {
        console.log('🔔 Service worker requested ringtone play');
        resetCallAccepted();
        playRingtone();
        return;
      }
      
      if (type === 'STOP_RINGTONE') {
        console.log('🔕 Service worker requested ringtone stop');
        stopRingtone();
        return;
      }
      
      if (type === 'AUDIO_CALL_ACCEPT' && data?.from) {
        console.log('📞 Audio call accepted from notification:', data.from);

        // If this notification corresponds to a call we *just* hung up, the
        // OS-level notification was still on screen and the click is a stale
        // accept — ignore it so it doesn't re-open the incoming call UI.
        if (isRecentlyEnded({ from: data.from, callId: data.callId })) {
          console.log('🚫 Ignoring stale AUDIO_CALL_ACCEPT for recently ended call');
          stopRingtone();
          clearCallNotificationsInSW(data.from);
          return;
        }

        // Stop ringtone when accepting from notification
        stopRingtone();
        autoAcceptCall.current = true;
        peerIdRef.current = data.from;
        setCallerId(data.from);
        setIsIncomingCall(true);
        setCallStatus('incoming');
        
        setTimeout(() => {
          acceptCall();
        }, 100);
      } else if (type === 'AUDIO_CALL_REJECT' && data?.from) {
        console.log('📞 Audio call rejected from notification:', data.from);
        // Stop ringtone when rejecting from notification
        stopRingtone();
        autoAcceptCall.current = false;
        const rejectCall = () => {
          if (socket && socket.connected) {
            socket.emit('audioCallRejected', { 
              from: user.id, 
              to: data.from,
              callId: data.callId || callIdRef.current
            });
            console.log('✅ Rejection sent to backend');
          } else {
            const checkSocket = setInterval(() => {
              if (socket && socket.connected) {
                clearInterval(checkSocket);
                socket.emit('audioCallRejected', { 
                  from: user.id, 
                  to: data.from,
                  callId: data.callId || callIdRef.current
                });
                console.log('✅ Rejection sent to backend after socket ready');
              }
            }, 100);
            setTimeout(() => clearInterval(checkSocket), 5000);
          }
        };
        rejectCall();
        setIsIncomingCall(false);
        setCallStatus('ended');
        handleEndCall();
      }
    };

    navigator.serviceWorker.addEventListener('message', handleServiceWorkerMessage);

    return () => {
      navigator.serviceWorker.removeEventListener('message', handleServiceWorkerMessage);
    };
  }, [socket, user.id]);

  // Check URL for audio call parameter (when app opens from notification)
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const audioCallFrom = urlParams.get('audioCallFrom');
    const action = urlParams.get('action');
    
    if (audioCallFrom) {
      console.log('📞 App opened with audio call from:', audioCallFrom, 'action:', action);
      window.history.replaceState({}, '', window.location.pathname);
      
      if (action === 'reject') {
        console.log('📞 Rejecting call from notification URL');
        autoAcceptCall.current = false;
        const sendRejection = () => {
          if (socket && socket.connected) {
            socket.emit('audioCallRejected', { 
              from: user.id, 
              to: audioCallFrom,
              callId: callIdRef.current
            });
            console.log('✅ Rejection sent to backend from URL');
          } else {
            setTimeout(sendRejection, 500);
          }
        };
        sendRejection();
        setIsIncomingCall(false);
        setCallStatus('ended');
      }
    }
  }, [socket, user.id]);

  // Stop ringtone when call status changes from incoming to connecting/connected
  useEffect(() => {
    if (callStatus === 'connecting' || callStatus === 'connected') {
      // Ensure ringtone is stopped when call is accepted/connected
      stopRingtone();
    }
  }, [callStatus, stopRingtone]);

  // Stop ringtone when incoming call flag changes to false
  useEffect(() => {
    if (!isIncomingCall && callStatus !== 'incoming') {
      // Ensure ringtone is stopped when incoming call ends
      stopRingtone();
    }
  }, [isIncomingCall, callStatus, stopRingtone]);

  useEffect(() => {
    if (!socket) return;

    const prepareIncomingState = async ({ callerId, callerName, offer, callId }) => {
      if (!callerId) return;

      // Drop signaling that belongs to a call we just disconnected from. This
      // covers the race where, right after we hang up, a queued incoming-call
      // event lands in the socket buffer and would otherwise re-show the
      // Accept/Reject modal for a call that's already over.
      if (isRecentlyEnded({ from: callerId, callId })) {
        console.log('🚫 Ignoring stale incoming audio call for recently ended call from', callerId);
        return;
      }

      // BUSY: if we are already in a call with someone else, surface this as a call-waiting
      // notification (Accept/Reject) rather than overwriting the active call's state.
      const liveStatus = callStatusRef.current;
      const currentPeerId = peerIdRef.current?.toString();
      const incomingFromId = callerId?.toString();
      const inActiveCall = liveStatus === 'connected' || liveStatus === 'calling' || liveStatus === 'connecting';
      if (inActiveCall && incomingFromId && incomingFromId !== currentPeerId) {
        console.warn('🔔 AudioCall busy: showing call-waiting notification for', incomingFromId, '(currently with', currentPeerId, ')');
        const existing = waitingCallRef.current;
        const waitingName = existing?.from === incomingFromId
          ? existing.fromName
          : (callerName || (await fetchUserName(incomingFromId)));
        setWaitingCallSafe({
          from: incomingFromId,
          fromName: waitingName || incomingFromId,
          offer: offer || existing?.offer,
          callId: callId || existing?.callId || null,
        });
        return;
      }

      const resolvedCallId = callId || callIdRef.current || generateCallId();
      if (offer) {
        pendingOffer.current = { offer, from: callerId, callId: resolvedCallId };
      }

      callIdRef.current = resolvedCallId;
      peerIdRef.current = callerId;
      setCallerId(callerId);
      setCallStatus('incoming');
      setError(null);
      const name = callerName || (await fetchUserName(callerId));
      setCallerName(name);
      setRemoteName(name);

      console.log('📲 Incoming audio call from:', callerId, '- waiting for Accept (callId:', resolvedCallId, ')');
      setIsIncomingCall(true);

      // Reset call accepted flag for new incoming call
      resetCallAccepted();

      // Play ringtone for incoming call (only for receiver, not caller)
      // Only play if call status is incoming (not already connecting/connected)
      if (callStatus === 'idle' || callStatus === 'incoming') {
        playRingtone();
      }

      if (autoAcceptCall.current) {
        console.log('🔔 Auto-accept flag detected - processing accept workflow');
        setTimeout(() => acceptCall(), 50);
      }
    };

    const onIncomingCall = async (data = {}) => {
      await prepareIncomingState({
        callerId: data.callerId || data.from,
        callerName: data.callerName,
        offer: data.offer,
        callId: data.callId,
      });
    };

    const onIncomingLegacy = async (data = {}) => {
      await prepareIncomingState({
        callerId: data.from,
        callerName: data.callerName,
        offer: pendingOffer.current?.offer || null,
        callId: data.callId,
      });
    };

    const onAccepted = (data = {}) => {
      if (data.callId && callIdRef.current && data.callId !== callIdRef.current) {
        return;
      }
      // Stop ringtone when call is accepted
      stopRingtone();
      setCallStatus('connecting');
    };

    const onRejected = (data = {}) => {
      if (data.callId && callIdRef.current && data.callId !== callIdRef.current) {
        return;
      }
      // Stop ringtone when call is rejected
      stopRingtone();
      callEndReasonRef.current = 'rejected';
      setCallStatus('ended');
      setError('Call was rejected');
      handleEndCall();
    };

    const onEnded = (data = {}) => {
      // If a HELD peer ended the call, just clean up the held slot.
      const fromId = data?.from?.toString();
      const held = heldCallRef.current;
      if (fromId && held && fromId === held.peerId?.toString()) {
        try { if (held.pc && held.pc.signalingState !== 'closed') held.pc.close(); } catch (_) {}
        heldCallRef.current = null;
        setHasHeldCall(false);
        setHeldCallName('');
        return;
      }
      if (data.callId && callIdRef.current && data.callId !== callIdRef.current) {
        return;
      }
      // Stop ringtone when call ends (caller cancelled)
      stopRingtone();
      setCallStatus('ended');
      handleEndCall();
    };

    const onIce = async (data = {}) => {
      if (!data.candidate) return;
      // Route ICE to the right PC: held PC vs active PC.
      const fromId = data?.from?.toString();
      const held = heldCallRef.current;
      let targetPc = peerConnectionRef.current || peerConnection;
      if (fromId && held && fromId === held.peerId?.toString()) {
        targetPc = held.pc;
      } else if (data.callId && callIdRef.current && data.callId !== callIdRef.current) {
        // Active call's callId mismatch — but maybe it's for the held one.
        if (held && data.callId === held.callId) {
          targetPc = held.pc;
        } else {
          console.log('🚫 Ignoring ICE candidate for stale callId', data.callId);
          return;
        }
      }
      if (!targetPc) {
        pendingIceCandidates.current.push({
          candidate: data.candidate,
          callId: data.callId || callIdRef.current,
        });
        return;
      }
      if (targetPc.signalingState === 'closed') {
        return;
      }
      if (targetPc.remoteDescription) {
        try {
          await targetPc.addIceCandidate(new RTCIceCandidate(data.candidate));
        } catch (err) {
          console.error('AudioCall: addIceCandidate error', err);
        }
      } else {
        pendingIceCandidates.current.push({
          candidate: data.candidate,
          callId: data.callId || callIdRef.current,
        });
      }
    };

    const onOffer = (data = {}) => {
      if (!data.offer || !data.from) return;

      // Drop offers that belong to a call we just hung up on.
      if (isRecentlyEnded({ from: data.from, callId: data.callId })) {
        console.log('🚫 Ignoring stale audio offer for recently ended call from', data.from);
        return;
      }

      // Refresh rejoin from the person we're already talking to. Answer in place.
      if (data.resume) {
        const resumeFrom = data.from?.toString();
        const resumePeer = peerIdRef.current?.toString();
        const resumeStatus = callStatusRef.current;
        const stillHere = resumeStatus === 'connected' || resumeStatus === 'calling' || resumeStatus === 'connecting';
        if (stillHere && resumeFrom && resumeFrom === resumePeer) {
          if (data.callId) callIdRef.current = data.callId;
          console.log('🔄 Accepting audio rejoin offer from', resumeFrom);
          setError('Reconnecting…');
          handleOffer({ offer: data.offer, from: data.from, callId: data.callId });
        } else {
          console.log('🚫 Ignoring audio rejoin offer; call is no longer active');
        }
        return;
      }

      // BUSY: stash incoming offer onto the call-waiting record so the user can Accept/Reject
      // it from the banner. Do NOT overwrite the active call's pendingOffer/peerIdRef.
      const liveStatus = callStatusRef.current;
      const currentPeerId = peerIdRef.current?.toString();
      const incomingFromId = data.from?.toString();
      const inActiveCall = liveStatus === 'connected' || liveStatus === 'calling' || liveStatus === 'connecting';
      if (inActiveCall && incomingFromId && incomingFromId !== currentPeerId) {
        console.warn('🔔 AudioCall busy: stashing offer from', incomingFromId, 'as waiting call (currently with', currentPeerId, ')');
        const existing = waitingCallRef.current;
        setWaitingCallSafe({
          from: incomingFromId,
          fromName: existing?.fromName || incomingFromId,
          offer: data.offer,
          callId: data.callId || existing?.callId || null,
        });
        return;
      }

      const resolvedCallId = data.callId || callIdRef.current || generateCallId();
      pendingOffer.current = { offer: data.offer, from: data.from, callId: resolvedCallId };
      if (!callIdRef.current) {
        callIdRef.current = resolvedCallId;
      }
      peerIdRef.current = data.from;
      console.log('📦 Offer stored for call', resolvedCallId, '- awaiting Accept');

      // Play ringtone when offer is received (if we haven't already and call is not accepted)
      // This ensures ringtone plays even if incomingAudioCall event was missed
      // But don't play if call is already connecting/connected/ended
      if (!isIncomingCall && callStatus !== 'incoming' && callStatus !== 'connecting' && callStatus !== 'connected' && callStatus !== 'ended') {
        console.log('🔔 Playing ringtone on offer receipt');
        setCallStatus('incoming');
        setIsIncomingCall(true);
        setCallerId(data.from);
        playRingtone();
      }

      if (autoAcceptCall.current) {
        console.log('🔔 Auto-accept flag detected - running accept workflow');
        setTimeout(() => acceptCall(), 50);
      }
    };

    const onAnswer = async (data = {}) => {
      if (data.callId && callIdRef.current && data.callId !== callIdRef.current) {
        console.log('🚫 Ignoring answer for stale callId', data.callId);
        return;
      }
      await handleAnswer(data.answer);
    };

    socket.on('incoming-call', onIncomingCall);
    socket.on('incomingAudioCall', onIncomingLegacy);
    socket.on('audioCallAccepted', onAccepted);
    socket.on('audioCallRejected', onRejected);
    socket.on('audioCallEnded', onEnded);
    socket.on('audioIceCandidate', onIce);
    socket.on('audioOffer', onOffer);
    socket.on('audioAnswer', onAnswer);

    // ---- Multi-participant audio (mesh conference) signaling ----
    const onIncomingMultiAudioCall = async (data = {}) => {
      const { from, callId, existingParticipants, callerName: incomingCallerName } = data;

      // Drop invites that belong to a call we just hung up on.
      if (isRecentlyEnded({ from, callId })) {
        console.log('🚫 Ignoring stale incoming multi-audio call from', from);
        return;
      }

      // The invite is the most authoritative source of the current participant
      // list (server folds in the inviter's `existingParticipants` AFTER any
      // sibling-invitee `multiAudioCallOffer`s have been processed). Remember
      // it so `acceptCall` -> `handleMultiAudioCallOffer` can mesh with every
      // actual participant on accept, not just the inviter.
      recordInviteParticipants(from, existingParticipants);

      if (callId) callIdRef.current = callId;
      isMultiParticipantCallRef.current = true;

      // If we're already in an active call, ignore — `multiAudioCallParticipantJoined`
      // + `multiAudioCallOffer` will heal the mesh for existing participants.
      const liveStatus = callStatusRef.current;
      if (liveStatus === 'connected' || liveStatus === 'calling' || liveStatus === 'connecting') {
        return;
      }

      setCallerId(from);
      setCallStatus('incoming');
      setError(null);
      const name = await fetchUserName(from);
      setCallerName(name || incomingCallerName || 'Someone');
      setRemoteName(name || incomingCallerName || 'Someone');
      peerIdRef.current = from;
      resetCallAccepted();
      playRingtone();
      setIsIncomingCall(true);
    };

    const onMultiAudioCallOffer = async (data = {}) => {
      const { from, offer, callId, existingParticipants } = data;
      const myId = user.id?.toString();
      const fromId = from?.toString();

      // Drop offers that belong to a call we just hung up on.
      if (isRecentlyEnded({ from, callId })) {
        console.log('🚫 Ignoring stale multi-audio offer from', fromId);
        return;
      }

      if (callId) callIdRef.current = callId;

      const liveStatus = callStatusRef.current;
      const isActivelyInCall = (liveStatus === 'connected' || liveStatus === 'calling' || isMultiParticipantCallRef.current) && localStreamRef.current;

      if (isActivelyInCall) {
        try {
          if (data.resume && fromId && fromId === peerIdRef.current?.toString() && peerConnectionRef.current) {
            const oldPrimary = peerConnectionRef.current;
            peerConnectionRef.current = null;
            try { oldPrimary.close(); } catch (_) {}
          }
          let pc = additionalPeerConnectionsRef.current.get(fromId);

          // Glare handling: if we both sent an offer, the higher ID rolls back.
          if (pc && pc.signalingState === 'have-local-offer') {
            const imPolite = myId > fromId;
            if (imPolite) {
              await pc.setLocalDescription({ type: 'rollback' });
            } else {
              return;
            }
          }

          const replacePc = !!data.resume || !pc || pc.signalingState === 'closed' || pc.connectionState === 'failed' || pc.connectionState === 'disconnected' || pc.connectionState === 'closed';
          if (replacePc) {
            if (pc) {
              try { pc.close(); } catch (_) { /* noop */ }
              additionalPeerConnectionsRef.current.delete(fromId);
            }
            pc = await createPeerConnectionWithParticipant(fromId, false);
          }
          if (!pc) throw new Error('Failed to create peer connection');

          // Correct responder order: setRemoteDescription → addTrack → createAnswer
          await pc.setRemoteDescription(new RTCSessionDescription(offer));

          // Add local tracks AFTER setRemoteDescription so transceivers match the offer
          const localAudioStream = localStreamRef.current;
          if (localAudioStream) {
            const existingSenders = pc.getSenders().filter(s => s.track);
            if (existingSenders.length === 0) {
              localAudioStream.getTracks().forEach(track => {
                pc.addTrack(track, localAudioStream);
              });
            }
          }

          await flushMultiIceCandidates(fromId, pc);
          const answer = await pc.createAnswer();
          await pc.setLocalDescription(answer);

          socket.emit('multiAudioCallAnswer', {
            from: myId,
            to: fromId,
            answer,
            callId,
          });

          // Mark as conference and ensure self + original peer are tracked
          isMultiParticipantCallRef.current = true;
          const primaryPeerId = peerIdRef.current?.toString();
          setAllParticipants((prev) => {
            const next = new Set(prev);
            next.add(fromId);
            if (myId) next.add(myId);
            if (primaryPeerId) next.add(primaryPeerId);
            return next;
          });
          // Ensure the original peer's stream appears in participant tiles
          if (primaryPeerId && remoteStreamRef.current && !remoteStreamsRef.current.has(primaryPeerId)) {
            upsertRemoteStream(primaryPeerId, remoteStreamRef.current);
          }
        } catch (err) {
          console.error(`❌ Error handling multi-audio offer from ${fromId}:`, err);
        }
        return;
      }

      // Union this event's participants with anything we already learned
      // from the matching `incomingMultiAudioCall` (the invite event), so
      // the joiner has the most complete list at accept time.
      recordInviteParticipants(from, existingParticipants);
      const knownParticipants = Array.from(
        multiCallInviteParticipantsRef.current.get(from?.toString()) || []
      );

      // Defensive: don't overwrite an existing pendingOffer from a different
      // inviter with a stray mesh offer from a sibling joiner. See the
      // matching guard in VideoCall.js for the full rationale.
      if (pendingOffer.current && pendingOffer.current.from && pendingOffer.current.from.toString() !== fromId) {
        console.log('🚫 Ignoring stray mesh audio offer from', fromId, '— already have pending offer from', pendingOffer.current.from);
        return;
      }

      // Initial invitation: store offer; trigger incoming UI if not already showing.
      pendingOffer.current = {
        offer,
        from,
        isMultiParticipant: true,
        existingParticipants: knownParticipants,
        callId,
      };

      if (callStatusRef.current === 'incoming' && from === peerIdRef.current) {
        return;
      }

      if (callStatusRef.current !== 'incoming' && callStatusRef.current !== 'connected' && callStatusRef.current !== 'calling') {
        setCallStatus('incoming');
        setIsIncomingCall(true);
        setCallerId(from);
        const name = await fetchUserName(from);
        setCallerName(name);
        setRemoteName(name);
        peerIdRef.current = from;
        playRingtone();
      }

      if (autoAcceptCall.current && from === peerIdRef.current) {
        autoAcceptCall.current = false;
        setIsIncomingCall(false);
        await handleMultiAudioCallOffer(offer, from, callId, knownParticipants);
      }
    };

    const onMultiAudioCallAnswer = async (data = {}) => {
      const { from, answer } = data;
      const fromId = from?.toString();
      const pc = additionalPeerConnectionsRef.current.get(fromId);
      if (!pc) return;
      if (pc.signalingState === 'closed') return;
      if (pc.signalingState !== 'have-local-offer') return;
      try {
        await pc.setRemoteDescription(new RTCSessionDescription(answer));
        await flushMultiIceCandidates(fromId, pc);
      } catch (err) {
        console.error('❌ Error setting multi-audio remote description for', fromId, ':', err);
      }
    };

    const onMultiAudioIceCandidate = async (data = {}) => {
      const { from, candidate } = data;
      const fromId = from?.toString();
      if (!candidate || !fromId) return;

      // Check additional PCs first, then fall back to the primary peerConnectionRef
      let pc = additionalPeerConnectionsRef.current.get(fromId);
      if (!pc && peerIdRef.current?.toString() === fromId && peerConnectionRef.current) {
        pc = peerConnectionRef.current;
      }

      if (pc && pc.remoteDescription) {
        try {
          await pc.addIceCandidate(new RTCIceCandidate(candidate));
        } catch (err) {
          console.error('❌ Error adding multi-audio ICE candidate for', fromId, ':', err);
        }
      } else {
        queueMultiIceCandidate(fromId, candidate);
      }
    };

    const onMultiAudioParticipantJoined = async (data = {}) => {
      const { newParticipantId, callId, existingParticipants } = data;
      const myId = user.id?.toString();
      if (callId) callIdRef.current = callId;
      isMultiParticipantCallRef.current = true;

      const normalizedExisting = Array.isArray(existingParticipants)
        ? existingParticipants.map((p) => p?.toString()).filter(Boolean)
        : [];
      const normalizedNew = newParticipantId?.toString();
      const participantsToEnsure = new Set([
        ...normalizedExisting,
        ...(normalizedNew ? [normalizedNew] : []),
      ]);

      setAllParticipants((prev) => {
        const next = new Set(prev);
        participantsToEnsure.forEach((pid) => next.add(pid));
        return next;
      });

      const primaryPeerId = peerIdRef.current?.toString();
      if (!localStreamRef.current) return;
      for (const pid of participantsToEnsure) {
        if (!pid || pid === myId) continue;
        const forceReconnect = !!data.resume && pid === normalizedNew;
        if (forceReconnect && pid === primaryPeerId && peerConnectionRef.current) {
          const oldPrimary = peerConnectionRef.current;
          peerConnectionRef.current = null;
          try { oldPrimary.close(); } catch (_) {}
        }
        if (!forceReconnect && pid === primaryPeerId && peerConnectionRef.current
          && peerConnectionRef.current.signalingState !== 'closed'
          && peerConnectionRef.current.connectionState !== 'failed'
          && peerConnectionRef.current.connectionState !== 'disconnected') {
          if (remoteStreamRef.current && !remoteStreamsRef.current.has(pid)) {
            upsertRemoteStream(pid, remoteStreamRef.current);
          }
          continue;
        }
        try {
          if (forceReconnect) {
            const stale = additionalPeerConnectionsRef.current.get(pid);
            if (stale) {
              try { stale.close(); } catch (_) {}
              additionalPeerConnectionsRef.current.delete(pid);
            }
          }
          const existingPc = additionalPeerConnectionsRef.current.get(pid);
          const needsConnection = forceReconnect || !existingPc
            || existingPc.signalingState === 'closed'
            || existingPc.connectionState === 'failed'
            || existingPc.connectionState === 'disconnected'
            || existingPc.connectionState === 'closed';
          if (needsConnection) {
            await createPeerConnectionWithParticipant(pid);
          }
        } catch (err) {
          console.error(`❌ Failed to ensure mesh audio link to ${pid}:`, err);
        }
      }
    };

    const onMultiAudioCallRejected = (data = {}) => {
      console.log('❌ Multi-audio invite rejected by:', data?.from);
    };

    const onMultiAudioCallParticipantLeft = (data = {}) => {
      const leftId = data?.participantId?.toString();
      if (!leftId) return;
      const pc = additionalPeerConnectionsRef.current.get(leftId);
      if (pc) {
        try { pc.close(); } catch (_) { /* noop */ }
        additionalPeerConnectionsRef.current.delete(leftId);
      }
      remoteStreamsRef.current.delete(leftId);
      removeRemoteAudioElement(leftId);
      setAllParticipants((prev) => {
        const next = new Set(prev);
        next.delete(leftId);
        return next;
      });
      syncRemoteParticipants();
    };

    socket.on('incomingMultiAudioCall', onIncomingMultiAudioCall);
    socket.on('multiAudioCallOffer', onMultiAudioCallOffer);
    socket.on('multiAudioCallAnswer', onMultiAudioCallAnswer);
    socket.on('multiAudioIceCandidate', onMultiAudioIceCandidate);
    socket.on('multiAudioCallParticipantJoined', onMultiAudioParticipantJoined);
    socket.on('multiAudioCallRejected', onMultiAudioCallRejected);
    socket.on('multiAudioCallParticipantLeft', onMultiAudioCallParticipantLeft);

    const onCallLimitReached = (data = {}) => {
      setError(data.message || `Conference call is limited to ${MAX_CALL_PARTICIPANTS} members.`);
    };
    socket.on('callLimitReached', onCallLimitReached);

    // Hold / resume from the remote peer.
    const onRemoteHold = (data = {}) => {
      const fromId = data?.from?.toString();
      if (!fromId) return;
      const activePeer = peerConnectionRef.current?._peerId || peerIdRef.current;
      if (fromId === activePeer?.toString()) setIsHeldByPeer(true);
    };
    const onRemoteResume = (data = {}) => {
      const fromId = data?.from?.toString();
      if (!fromId) return;
      const activePeer = peerConnectionRef.current?._peerId || peerIdRef.current;
      if (fromId === activePeer?.toString()) setIsHeldByPeer(false);
    };
    socket.on('audioCallHold', onRemoteHold);
    socket.on('audioCallResume', onRemoteResume);

    return () => {
      socket.off('incoming-call', onIncomingCall);
      socket.off('incomingAudioCall', onIncomingLegacy);
      socket.off('audioCallAccepted', onAccepted);
      socket.off('audioCallRejected', onRejected);
      socket.off('audioCallEnded', onEnded);
      socket.off('audioIceCandidate', onIce);
      socket.off('audioOffer', onOffer);
      socket.off('audioAnswer', onAnswer);
      socket.off('audioCallHold', onRemoteHold);
      socket.off('audioCallResume', onRemoteResume);
      socket.off('incomingMultiAudioCall', onIncomingMultiAudioCall);
      socket.off('multiAudioCallOffer', onMultiAudioCallOffer);
      socket.off('multiAudioCallAnswer', onMultiAudioCallAnswer);
      socket.off('multiAudioIceCandidate', onMultiAudioIceCandidate);
      socket.off('multiAudioCallParticipantJoined', onMultiAudioParticipantJoined);
      socket.off('multiAudioCallRejected', onMultiAudioCallRejected);
      socket.off('multiAudioCallParticipantLeft', onMultiAudioCallParticipantLeft);
      socket.off('callLimitReached', onCallLimitReached);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [socket, peerConnection]);

  // Track call direction and connection state for call-history logging.
  // This observes the existing callStatus transitions only — it never alters
  // them — so it captures direction (calling -> outgoing, incoming -> incoming)
  // and the moment the call actually connects, without touching signaling.
  useEffect(() => {
    if (callStatus === 'calling' && !callDirectionRef.current) {
      callDirectionRef.current = 'outgoing';
    } else if (callStatus === 'incoming' && !callDirectionRef.current) {
      callDirectionRef.current = 'incoming';
    }
    if (callStatus === 'connected' && !callConnectedRef.current) {
      callConnectedRef.current = true;
      callConnectedAtRef.current = Date.now();
    }
  }, [callStatus]);

  const startCallTimer = () => {
    if (durationTimerRef.current) {
      clearInterval(durationTimerRef.current);
      durationTimerRef.current = null;
    }
    const origin = callConnectedAtRef.current || Date.now();
    if (!callConnectedAtRef.current) callConnectedAtRef.current = origin;
    const tick = () => setCallDuration(Math.max(0, Math.round((Date.now() - origin) / 1000)));
    tick();
    durationTimerRef.current = setInterval(tick, 1000);
  };

  const stopCallTimer = () => {
    if (durationTimerRef.current) {
      clearInterval(durationTimerRef.current);
      durationTimerRef.current = null;
    }
  };

  const scheduleCallRecovery = (pc, { restartIce = false, quiet = false } = {}) => {
    if (!quiet) setError('Reconnecting…');
    if (restartIce) {
      try {
        reconnectAttemptsRef.current += 1;
        if (
          reconnectAttemptsRef.current <= 2 &&
          typeof pc.restartIce === 'function' &&
          pc.signalingState !== 'closed'
        ) {
          pc.restartIce();
        }
      } catch (_) {}
    }
    if (reconnectTimerRef.current) clearTimeout(reconnectTimerRef.current);
    reconnectTimerRef.current = setTimeout(() => {
      if (peerConnectionRef.current !== pc) return;
      const down = pc.connectionState === 'failed'
        || pc.connectionState === 'disconnected'
        || pc.connectionState === 'closed'
        || pc.iceConnectionState === 'failed'
        || pc.iceConnectionState === 'disconnected';
      if (!down) return;
      setError('Call connection failed');
      endCallRef.current?.();
    }, CALL_RECONNECT_GRACE_MS);
  };

  const initializePeerConnection = () => {
    console.log('🔗 Initializing RTCPeerConnection with ICE servers');
    
    // Clean up any existing connection — but never close the held call's PC.
    // Detach the ref first so the old PC's "closed" event can't end the new call.
    const current = peerConnectionRef.current || peerConnection;
    if (current && current !== heldCallRef.current?.pc) {
      peerConnectionRef.current = null;
      try {
        current.close();
      } catch (err) {
        console.warn('Previous peer connection close error:', err);
      }
    }
    
    const pc = new RTCPeerConnection({
      iceServers: config.iceServers,
      iceCandidatePoolSize: 10,
      bundlePolicy: 'max-bundle',
      rtcpMuxPolicy: 'require',
      iceTransportPolicy: config.forceRelay ? 'relay' : 'all',
    });

    pc.onicecandidate = (event) => {
      // Use this PC's own peerId so a held PC keeps routing trickle ICE to its peer
      // (not to whoever is currently active after a swap).
      const targetPeerId = pc._peerId || peerIdRef.current;
      if (event.candidate && targetPeerId) {
        console.log('🧊 WebRTC: ICE candidate discovered:', event.candidate.type);
        socket.emit('audioIceCandidate', {
          candidate: event.candidate,
          from: user.id,
          to: targetPeerId,
          callId: pc._callId || callIdRef.current
        });
      } else if (!event.candidate) {
        console.log('🧊 WebRTC: ICE gathering complete');
      }
    };

    pc.ontrack = (event) => {
      console.log('🎵 ontrack: Remote audio track received!', {
        kind: event.track.kind,
        enabled: event.track.enabled,
        readyState: event.track.readyState,
        muted: event.track.muted,
        streams: event.streams?.length
      });

      // If this PC is NOT the currently-active one (e.g. it's the held call), do NOT
      // overwrite global stream state. That would clobber the active call's audio.
      if (peerConnectionRef.current && peerConnectionRef.current !== pc) {
        console.log('⏸️ AudioCall: ontrack from non-active PC (held?), skipping global update');
        return;
      }
      
      let stream = null;
      if (event.streams && event.streams[0]) {
        stream = event.streams[0];
        console.log('✅ Using stream from event:', stream.id);
      } else if (event.track) {
        stream = new MediaStream([event.track]);
        console.log('⚠️ Created new stream from track:', stream.id);
      } else {
        console.error('❌ No streams or tracks in ontrack event!');
        return;
      }
      
      const audioTracks = stream.getAudioTracks();
      console.log('🎵 Remote stream audio tracks:', audioTracks.length);
      audioTracks.forEach((track, idx) => {
        console.log(`🎵 Track ${idx}:`, {
          kind: track.kind,
          enabled: track.enabled,
          readyState: track.readyState,
          muted: track.muted,
          id: track.id
        });
      });
      
      setRemoteStream(stream);
      remoteStreamRef.current = stream;
    };

    pc.onconnectionstatechange = () => {
      console.log('🔗 Connection state:', pc.connectionState);
      // Only the PC currently stored as the active call can end it.
      if (peerConnectionRef.current !== pc) return;
      if (pc.connectionState === 'connected') {
        console.log('✅ PEER CONNECTION ESTABLISHED!');
        setError(null);
        setCallStatus('connected');
        setIsCallActive(true);
        if (durationTimerRef.current === null) {
          startCallTimer();
        }
        if (reconnectTimerRef.current) {
          clearTimeout(reconnectTimerRef.current);
          reconnectTimerRef.current = null;
        }
      } else if (pc.connectionState === 'failed' || pc.connectionState === 'disconnected') {
        console.warn('⚠️ Connection', pc.connectionState);
        scheduleCallRecovery(pc, {
          restartIce: pc.connectionState === 'failed',
          quiet: pc.connectionState === 'disconnected',
        });
      } else if (pc.connectionState === 'closed') {
        console.log('🔌 Connection closed');
        endCallRef.current?.();
      }
    };

    pc.oniceconnectionstatechange = () => {
      console.log('🧊 ICE connection state:', pc.iceConnectionState);
      if (peerConnectionRef.current !== pc) return;
      
      if (pc.iceConnectionState === 'connected' || pc.iceConnectionState === 'completed') {
        console.log('✅ ICE CONNECTION SUCCESSFUL!');
        setError(null);
        if (reconnectTimerRef.current) {
          clearTimeout(reconnectTimerRef.current);
          reconnectTimerRef.current = null;
        }
      } else if (pc.iceConnectionState === 'failed' || pc.iceConnectionState === 'disconnected') {
        scheduleCallRecovery(pc, {
          restartIce: pc.iceConnectionState === 'failed',
          quiet: pc.iceConnectionState === 'disconnected',
        });
      }
    };

    pc.onicegatheringstatechange = () => {
      // ICE gathering state change
    };

    setPeerConnectionSafe(pc);
    return pc;
  };

  const startCall = async (overrideUser = null, options = null) => {
    const callTarget = overrideUser || selectedUser;
    if (!callTarget) {
      setError('Please select a user to call');
      return;
    }
    callTargetRef.current = callTarget;
    try {
      const left = await leaveOtherCallSessions(sessionIdRef.current);
      if (left > 0) await new Promise((resolve) => setTimeout(resolve, 200));
      cleanupDoneRef.current = false;
      pendingIceCandidates.current = [];
      pendingOffer.current = null;
      const activeCallId = options?.callId || generateCallId();
      callIdRef.current = activeCallId;
      console.log('🆔 Starting call with callId:', activeCallId);

      console.log('📞 AudioCall: startCall - Initiating call to', callTarget.username);
      setError(null);
      
      console.log('🎤 Requesting microphone access...');
      const stream = await navigator.mediaDevices.getUserMedia({ 
        video: false, 
        audio: {
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true,
          sampleRate: 48000,
          channelCount: 1
        }
      });
      
      console.log('✅ Got local audio stream:', {
        id: stream.id,
        tracks: stream.getAudioTracks().length,
        active: stream.active
      });
      
      setLocalStream(stream);
      localStreamRef.current = stream;
      if (options?.isMuted) {
        stream.getAudioTracks().forEach((track) => { track.enabled = false; });
        setIsMuted(true);
      }

      console.log('🔗 Initializing peer connection...');
      const pc = initializePeerConnection();
      pc._peerId = callTarget.id;
      pc._callId = activeCallId;
      
      // Add local tracks to peer connection BEFORE creating offer
      console.log('📤 Adding local audio tracks to peer connection...');
      stream.getTracks().forEach((track) => {
        const sender = pc.addTrack(track, stream);
        console.log('✅ Track added:', {
          kind: track.kind,
          enabled: track.enabled,
          transceiver: sender.track ? 'created' : 'none'
        });
      });

      console.log('📝 Creating SDP offer...');
      const offer = await pc.createOffer({ 
        offerToReceiveAudio: true,
        offerToReceiveVideo: false,
        voiceActivityDetection: true
      });
      
      console.log('✅ Offer created, setting as local description...');
      await pc.setLocalDescription(offer);
      console.log('✅ Local description set');

      setRemoteName(callTarget?.username || callTarget?.id || 'User');
      peerIdRef.current = callTarget.id;

      console.log('📤 Sending offer to receiver:', callTarget.username);
      socket.emit('audioCallOffer', {
        offer,
        from: user.id,
        to: callTarget.id,
        callId: activeCallId,
        callerName: user?.username || user?.name || user?.email || user.id,
        resume: !!options?.resume,
      });
      
      if (options?.resume && options?.wasConnected) {
        setIsCallActive(true);
        setCallStatus('connecting');
        startCallTimer();
      } else {
        setCallStatus('calling');
      }
      console.log('✅ Call initiated, waiting for answer...');
    } catch (err) {
      console.error('❌ AudioCall: startCall error', err);
      if (err.name === 'NotAllowedError') {
         setError('Microphone access denied. Please allow permissions.');
      } else if (err.name === 'NotFoundError') {
        setError('No microphone found. Please connect a device.');
      } else {
        setError('Failed to start call. Please try again.');
      }
    }
  };

  const handleOffer = async (payload = {}) => {
    try {
      const { offer, fromId, from, callId } = payload;
      if (!offer) {
        throw new Error('Missing SDP offer');
      }
      const remoteUserId = fromId || from || callerId || peerIdRef.current;
      const activeCallId = callId || callIdRef.current || generateCallId();
      callIdRef.current = activeCallId;
      cleanupDoneRef.current = false;

      console.log('📞 handleOffer: User accepted - NOW processing offer (getting microphone, creating peer connection)...');
      
      setError(null);
      
      console.log('🎤 Getting microphone access...');
      const stream = await navigator.mediaDevices.getUserMedia({ 
        video: false, 
        audio: {
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true,
          sampleRate: 48000,
          channelCount: 1
        }
      });
      
      console.log('✅ Got local audio stream:', {
        id: stream.id,
        tracks: stream.getAudioTracks().length,
        active: stream.active
      });
      
      setLocalStream(stream);
      localStreamRef.current = stream;

      console.log('🔗 Initializing peer connection...');
      const pc = initializePeerConnection();
      
      peerIdRef.current = remoteUserId;
      pc._peerId = remoteUserId;
      pc._callId = activeCallId;
      
      // CRITICAL FIX: Set remote description FIRST (creates transceivers from offer)
      console.log('📝 Setting remote description (offer)...');
      await pc.setRemoteDescription(new RTCSessionDescription(offer));
      console.log('✅ Remote description set');
      
      // THEN add local tracks (will upgrade transceivers to sendrecv)
      console.log('📤 Adding local audio tracks...');
      stream.getTracks().forEach((track) => {
        console.log('🎤 Track before adding:', {
          kind: track.kind,
          enabled: track.enabled,
          readyState: track.readyState,
          muted: track.muted
        });
        pc.addTrack(track, stream);
        console.log('✅ Track added to peer connection');
      });
      
      // Process pending ICE candidates
      console.log(`📡 AudioCall: Processing ${pendingIceCandidates.current.length} pending ICE candidates`);
      const queuedCandidates = pendingIceCandidates.current.filter(
        (item) => !item?.callId || !callIdRef.current || item.callId === callIdRef.current
      );
      pendingIceCandidates.current = [];
      for (const queued of queuedCandidates) {
        if (!queued?.candidate) continue;
        try {
          await pc.addIceCandidate(new RTCIceCandidate(queued.candidate));
          console.log('✅ Added pending ICE candidate');
        } catch (err) {
          console.error('❌ Error adding pending ICE candidate:', err);
        }
      }

      console.log('📝 Creating SDP answer...');
      const answer = await pc.createAnswer();
      
      console.log('✅ Answer created, setting as local description...');
      await pc.setLocalDescription(answer);
      console.log('✅ Local description (answer) set');

      console.log('📤 Sending answer to caller...');
      socket.emit('audioCallAnswer', {
        answer,
        from: user.id,
        to: remoteUserId,
        callId: activeCallId
      });

      setCallStatus('connecting');
      console.log('✅ Answer sent! Waiting for ICE and remote audio...');
    } catch (err) {
      console.error('❌ AudioCall: handleOffer error', err);
      setError('Failed to accept call. Please try again.');
      handleEndCall();
    }
  };

  const handleAnswer = async (answer) => {
    try {
      if (!peerConnection) {
        console.warn('⚠️ No peer connection available for answer');
        return;
      }
      
      const currentState = peerConnection.signalingState;
      console.log('📥 Received answer, current signaling state:', currentState);
      
      if (currentState !== 'have-local-offer') {
        console.warn(`⚠️ Cannot set answer in state: ${currentState}. Expected 'have-local-offer'`);
        return;
      }
      
      if (peerConnection.remoteDescription) {
        console.warn('⚠️ Remote description already set, ignoring duplicate answer');
        return;
      }
      
      console.log('✅ Setting remote description (answer)...');
      await peerConnection.setRemoteDescription(new RTCSessionDescription(answer));
      console.log('✅ Remote description (answer) set successfully');
      setCallStatus((prev) => (prev === 'connected' ? prev : 'connecting'));
        
      // Process pending ICE candidates
      console.log(`📡 AudioCall: Processing ${pendingIceCandidates.current.length} pending ICE candidates`);
      const queuedCandidates = pendingIceCandidates.current.filter(
        (item) => !item?.callId || !callIdRef.current || item.callId === callIdRef.current
      );
      pendingIceCandidates.current = [];
      for (const queued of queuedCandidates) {
        if (!queued?.candidate) continue;
        try {
          await peerConnection.addIceCandidate(new RTCIceCandidate(queued.candidate));
          console.log('✅ Added pending ICE candidate');
        } catch (err) {
          console.error('❌ Error adding pending ICE candidate:', err);
        }
      }

      console.log('✅ Answer processed, audio will play via ontrack handler');
    } catch (err) {
      console.error('❌ AudioCall: handleAnswer error', err);
      setError('Failed to establish call connection.');
    } 
  };

  const acceptCall = async () => {
    const offerPayload = pendingOffer.current;
    if (!offerPayload) {
      console.warn('⚠️ Accept clicked but offer not ready yet');
      return;
    }

    const remoteUserId = offerPayload.from || callerId;
    if (!remoteUserId) {
      setError('Unknown caller. Please try again.');
      return;
    }

    const left = await leaveOtherCallSessions(sessionIdRef.current);
    if (left > 0) await new Promise((resolve) => setTimeout(resolve, 200));

    // End any active video call before accepting this audio call.
    window.dispatchEvent(new CustomEvent('endOtherCallTypes', { detail: { acceptedType: 'audio' } }));

    // Stop ringtone IMMEDIATELY when user accepts the call
    console.log('🛑 Stopping ringtone - call accepted');
    stopRingtone();
    setIsIncomingCall(false); // Set this early to prevent ringtone from restarting

    const activeCallId = offerPayload.callId || callIdRef.current || generateCallId();
    callIdRef.current = activeCallId;
    console.log('✅ AudioCall: User accepted callId', activeCallId, 'from', remoteUserId);

    try {
      // Multi-participant audio invite path: do NOT emit `audioCallAccepted` (1:1 event);
      // handleMultiAudioCallOffer emits `multiAudioCallAccepted` with the full participants list.
      if (offerPayload.isMultiParticipant) {
        await handleMultiAudioCallOffer(
          offerPayload.offer,
          offerPayload.from,
          activeCallId,
          offerPayload.existingParticipants || []
        );
        pendingOffer.current = null;
        autoAcceptCall.current = false;
        return;
      }

      socket.emit('audioCallAccepted', { from: user.id, to: remoteUserId, callId: activeCallId });
      setIsIncomingCall(false);
      setCallStatus('connecting');

      console.log('🎤 User accepted - NOW getting microphone and processing offer...');
      await handleOffer({
        offer: offerPayload.offer,
        from: remoteUserId,
        callId: activeCallId,
      });
      pendingOffer.current = null;
      autoAcceptCall.current = false;
      console.log('✅ Offer processed successfully, audio should flow both ways now');
    } catch (err) {
      console.error('❌ Error accepting call:', err);
      setError('Failed to accept call: ' + err.message);
      endCall();
    }
  };

  const rejectCall = () => {
    if (!callerId) return;
    // Stop ringtone when user rejects the call
    stopRingtone();
    if (pendingOffer.current?.isMultiParticipant) {
      socket.emit('multiAudioCallRejected', {
        from: user.id,
        to: callerId,
        callId: callIdRef.current,
      });
    } else {
      socket.emit('audioCallRejected', { from: user.id, to: callerId, callId: callIdRef.current });
    }
    setIsIncomingCall(false);
    setCallStatus('ended');
    setError('Call rejected');
    pendingOffer.current = null;
    handleEndCall();
  };

  // ---- Call waiting (incoming audio call while already in another call) ----
  const rejectWaitingCall = () => {
    const w = waitingCallRef.current;
    if (!w) return;
    try {
      if (socket && socket.connected) {
        socket.emit('audioCallRejected', { from: user.id, to: w.from, callId: w.callId || null });
      }
    } catch (_) { /* best effort */ }
    setWaitingCallSafe(null);
  };

  // ---- Call hold (telephone-style) ----
  const holdActivePeer = async () => {
    const pc = peerConnectionRef.current || peerConnection;
    if (!pc) return null;
    const savedTracks = new Map();
    for (const sender of pc.getSenders()) {
      if (sender?.track) {
        savedTracks.set(sender, sender.track);
        try { await sender.replaceTrack(null); } catch (err) { console.warn('audio hold: replaceTrack(null) failed', err); }
      }
    }
    // Mute incoming audio from held peer so the holder cannot hear them until swap.
    const mutedReceiverTracks = [];
    try {
      for (const receiver of pc.getReceivers()) {
        const t = receiver?.track;
        if (t && t.enabled) {
          mutedReceiverTracks.push(t);
          t.enabled = false;
        }
      }
    } catch (err) { console.warn('audio hold: mute receivers failed', err); }
    const snapshot = {
      pc,
      peerId: pc._peerId || peerIdRef.current,
      name: remoteName || callerName || 'Unknown',
      remoteStream: remoteStreamRef.current,
      callId: pc._callId || callIdRef.current,
      savedTracks,
      mutedReceiverTracks,
    };
    if (snapshot.peerId) {
      try {
        socket.emit('audioCallHold', { from: user.id, to: snapshot.peerId, callId: snapshot.callId });
      } catch (_) { /* best effort */ }
    }
    return snapshot;
  };

  const restoreHeldPeer = async (snapshot) => {
    if (!snapshot) return;
    for (const [sender, track] of snapshot.savedTracks) {
      try { await sender.replaceTrack(track); } catch (err) { console.warn('audio resume: replaceTrack failed', err); }
    }
    if (Array.isArray(snapshot.mutedReceiverTracks)) {
      for (const t of snapshot.mutedReceiverTracks) {
        try { t.enabled = true; } catch (_) { /* ignore */ }
      }
    }
    if (snapshot.peerId) {
      try {
        socket.emit('audioCallResume', { from: user.id, to: snapshot.peerId, callId: snapshot.callId });
      } catch (_) { /* best effort */ }
    }
  };

  const swapHeldCall = async () => {
    const held = heldCallRef.current;
    if (!held) return;
    const newlyHeld = await holdActivePeer();
    await restoreHeldPeer(held);
    setPeerConnectionSafe(held.pc);
    peerIdRef.current = held.peerId;
    callIdRef.current = held.callId;
    setRemoteName(held.name);
    setCallerName(held.name);
    setCallerId(held.peerId);

    // Build a fresh stream from the restored PC's live receiver tracks.
    // The snapshot's remoteStream may be stale (tracks ended while on hold).
    let activeStream = null;
    try {
      const liveTracks = held.pc.getReceivers()
        .map(r => r.track)
        .filter(t => t && t.readyState === 'live');
      if (liveTracks.length) {
        activeStream = new MediaStream(liveTracks);
      }
    } catch (_) { /* best effort */ }
    // Fall back to the snapshot stream if no live tracks yet.
    if (!activeStream) {
      activeStream = held.remoteStream;
    }
    remoteStreamRef.current = activeStream;
    setRemoteStream(activeStream);

    // Imperatively update the audio element — React does not bind srcObject.
    // Clear first so the browser detects the change even if it's the same object.
    if (remoteAudioRef.current) {
      remoteAudioRef.current.srcObject = null;
      remoteAudioRef.current.srcObject = activeStream;
      if (activeStream) {
        remoteAudioRef.current.play().catch(() => {});
      }
    }

    heldCallRef.current = newlyHeld || null;
    if (newlyHeld) {
      setHasHeldCall(true);
      setHeldCallName(newlyHeld.name);
    } else {
      setHasHeldCall(false);
      setHeldCallName('');
    }
  };

  const endHeldCall = () => {
    const held = heldCallRef.current;
    if (!held) return;
    if (held.peerId) {
      try {
        socket.emit('audioCallEnded', { from: user.id, to: held.peerId, callId: held.callId });
      } catch (_) { /* best effort */ }
    }
    try { if (held.pc && held.pc.signalingState !== 'closed') held.pc.close(); } catch (_) {}
    heldCallRef.current = null;
    setHasHeldCall(false);
    setHeldCallName('');
  };

  // Leave a conference call cleanly so we can accept a new incoming call.
  // Notifies all conference peers, closes mesh PCs, and resets conference state
  // WITHOUT resetting isCallActive (the new call will keep us active).
  const leaveConferenceForNewCall = () => {
    if (!isMultiParticipantCallRef.current) return;
    console.log('📞 AudioCall: leaving conference to accept a new call');

    const peerId = peerIdRef.current || selectedUser?.id || callTargetRef.current?.id;
    // Notify all conference peers
    if (peerId && socket?.connected) {
      socket.emit('audioCallEnded', { from: user.id, to: peerId, callId: callIdRef.current });
    }
    additionalPeerConnectionsRef.current.forEach((_pc, participantId) => {
      if (participantId !== peerId?.toString() && socket?.connected) {
        socket.emit('audioCallEnded', { from: user.id, to: participantId, callId: callIdRef.current });
      }
    });

    // Close main peer connection
    if (peerConnection) {
      try { peerConnection.close(); } catch (_) {}
    }

    // Close all mesh peer connections
    additionalPeerConnectionsRef.current.forEach((pc) => {
      try { if (pc && pc.signalingState !== 'closed') pc.close(); } catch (_) {}
    });
    additionalPeerConnectionsRef.current.clear();
    pendingMultiIceCandidatesRef.current.clear();

    // Stop and remove all per-participant audio elements
    multiRemoteAudioElsRef.current.forEach((el) => {
      try { el.pause(); } catch (_) {}
      try { el.srcObject = null; } catch (_) {}
      try { el.remove(); } catch (_) {}
    });
    multiRemoteAudioElsRef.current.clear();

    // Stop local/remote streams (new call will re-acquire)
    if (localStreamRef.current) {
      localStreamRef.current.getTracks().forEach((t) => t.stop());
      localStreamRef.current = null;
    }
    if (remoteStreamRef.current) {
      remoteStreamRef.current.getTracks().forEach((t) => t.stop());
      remoteStreamRef.current = null;
    }
    if (remoteAudioRef.current) {
      try { remoteAudioRef.current.srcObject = null; } catch (_) {}
    }

    // Clear conference-specific state
    remoteStreamsRef.current.clear();
    setRemoteParticipantStreams([]);
    setAllParticipants(new Set());
    isMultiParticipantCallRef.current = false;
    setShowAddParticipants(false);
    setSelectedContacts(new Set());
    stopCallTimer();
  };

  const acceptWaitingCall = async () => {
    const w = waitingCallRef.current;
    if (!w) return;

    const left = await leaveOtherCallSessions(sessionIdRef.current);
    if (left > 0) await new Promise((resolve) => setTimeout(resolve, 200));

    if (isMultiParticipantCallRef.current) {
      // Leave the conference — can't hold a mesh call
      leaveConferenceForNewCall();
    } else {
      // Existing hold logic for 1:1 calls
      // Only one held call slot — end any prior held call first.
      if (heldCallRef.current && heldCallRef.current.peerId !== w.from) {
        endHeldCall();
      }

      // 1) Hold the current active call (keep PC alive, mute outgoing audio, notify peer).
      const snapshot = await holdActivePeer();
      if (snapshot) {
        heldCallRef.current = snapshot;
        setHasHeldCall(true);
        setHeldCallName(snapshot.name || 'Unknown');
      }
    }

    // 2) Switch state for the new caller.
    setError(null);
    cleanupDoneRef.current = false;
    setRemoteStream(null);
    remoteStreamRef.current = null;
    if (remoteAudioRef.current) {
      try { remoteAudioRef.current.srcObject = null; } catch (_) {}
    }
    const newCallId = w.callId || generateCallId();
    callIdRef.current = newCallId;
    peerIdRef.current = w.from;
    setCallerId(w.from);
    setCallerName(w.fromName);
    setRemoteName(w.fromName);
    setCallStatus('incoming');
    setIsIncomingCall(false);
    setWaitingCallSafe(null);

    if (w.offer) {
      pendingOffer.current = { offer: w.offer, from: w.from, callId: newCallId };
      try {
        socket.emit('audioCallAccepted', { from: user.id, to: w.from, callId: newCallId });
      } catch (_) { /* best effort */ }
      try {
        await handleOffer({ offer: w.offer, from: w.from, callId: newCallId });
        pendingOffer.current = null;
      } catch (err) {
        console.error('❌ Error accepting waiting call:', err);
        setError('Failed to accept call: ' + err.message);
      }
    } else {
      // Offer hasn't arrived yet — auto-accept once it does.
      autoAcceptCall.current = true;
    }
  };

  const endCall = () => {
    // In multi-participant mode, emit a single multiAudioCallEnd so the server can broadcast
    // multiAudioCallParticipantLeft to every remaining participant. The rest of the conference
    // stays active; only OUR audio is dropped from their side.
    if (isMultiParticipantCallRef.current && callIdRef.current && socket && socket.connected) {
      try {
        socket.emit('multiAudioCallEnd', {
          from: user.id,
          callId: callIdRef.current,
        });
      } catch (_) { /* best effort */ }
    } else {
      const peerId = peerIdRef.current || selectedUser?.id || callTargetRef.current?.id;
      if (peerId) {
        socket.emit('audioCallEnded', { from: user.id, to: peerId, callId: callIdRef.current });
      }
    }
    handleEndCall();
  };
  endCallRef.current = endCall;

  const handleEndCall = () => {
    if (isPageUnloading()) return;
    if (cleanupDoneRef.current) return;
    cleanupDoneRef.current = true;
    clearActiveCall('audio');
    
    console.log('🎵 AudioCall: Ending call and cleaning up...');

    // Remember which peer/call we just disconnected from so we can drop any
    // late-arriving "incoming call" signals (socket replays + stale OS
    // notification clicks) from the same caller for a few seconds.
    const endedPeerId = (peerIdRef.current || callerId || selectedUser?.id || callTargetRef.current?.id || null);
    const endedCallId = callIdRef.current;
    markRecentlyEnded(endedPeerId, endedCallId);
    if (isMultiParticipantCallRef.current) {
      additionalPeerConnectionsRef.current.forEach((_pc, pid) => markRecentlyEnded(pid, endedCallId));
    }

    // Close any system push notifications for this caller so a later click
    // can't re-trigger AUDIO_CALL_ACCEPT.
    clearCallNotificationsInSW(endedPeerId);

    // Stop ringtone when call ends
    stopRingtone();
    // Reset flag for next call
    resetCallAccepted();
    
    // Log call to history before cleanup
    const callTargetForLog = selectedUser || callTargetRef.current;
    if (onEndCall && (callTargetForLog || callerId)) {
      const targetUser = callTargetForLog || { id: callerId, username: callerName };
      const direction = callDirectionRef.current || (callStatus === 'incoming' ? 'incoming' : 'outgoing');

      // Derive the call-history status without affecting any call flow.
      const wasConnected = callConnectedRef.current;
      let callHistoryStatus;
      if (wasConnected) callHistoryStatus = 'answered';
      else if (callEndReasonRef.current === 'rejected') callHistoryStatus = 'rejected';
      else callHistoryStatus = 'missed';
      const callHistoryDuration = wasConnected && callConnectedAtRef.current
        ? Math.max(0, Math.round((Date.now() - callConnectedAtRef.current) / 1000))
        : 0;

      onEndCall({
        userId: targetUser.id,
        userName: targetUser.username || remoteName || 'Unknown User',
        type: 'audio',
        direction: direction,
        status: callHistoryStatus,
        duration: callHistoryDuration,
        callId: endedCallId,
        isMultiParticipant: isMultiParticipantCallRef.current
      });
    }
    
    stopCallTimer();
    if (reconnectTimerRef.current) {
      clearTimeout(reconnectTimerRef.current);
      reconnectTimerRef.current = null;
    }
    reconnectAttemptsRef.current = 0;
    
    // Stop local tracks
    if (localStreamRef.current) {
      localStreamRef.current.getTracks().forEach((t) => t.stop());
    }
    
    // Stop remote tracks
    if (remoteStreamRef.current) {
      remoteStreamRef.current.getTracks().forEach((t) => t.stop());
    }
    
    // Clean audio elements
    if (remoteAudioRef.current) {
      try { 
        remoteAudioRef.current.pause(); 
        remoteAudioRef.current.srcObject = null;
      } catch (_) {}
    }
    if (localAudioRef.current) {
      try { 
        localAudioRef.current.pause(); 
        localAudioRef.current.srcObject = null;
      } catch (_) {}
    }
    
    // Close peer connection
    if (peerConnection) {
      try {
        peerConnection.close();
      } catch (err) {
        console.warn('PC close error:', err);
      }
    }

    // Close all additional peer connections (multi-participant mesh).
    additionalPeerConnectionsRef.current.forEach((pc, _participantId) => {
      try { if (pc && pc.signalingState !== 'closed') pc.close(); } catch (_) { /* noop */ }
    });
    additionalPeerConnectionsRef.current.clear();
    pendingMultiIceCandidatesRef.current.clear();

    // Stop and remove all per-participant audio elements.
    multiRemoteAudioElsRef.current.forEach((el) => {
      try { el.pause(); } catch (_) { /* noop */ }
      try { el.srcObject = null; } catch (_) { /* noop */ }
      try { el.remove(); } catch (_) { /* noop */ }
    });
    multiRemoteAudioElsRef.current.clear();
    remoteStreamsRef.current.clear();
    setRemoteParticipantStreams([]);
    setAllParticipants(new Set());
    isMultiParticipantCallRef.current = false;
    setShowAddParticipants(false);
    setSelectedContacts(new Set());

    // Also close any held call PC and notify the held peer.
    const held = heldCallRef.current;
    if (held) {
      if (held.peerId) {
        try {
          if (socket && socket.connected) {
            socket.emit('audioCallEnded', { from: user.id, to: held.peerId, callId: held.callId });
          }
        } catch (_) { /* best effort */ }
      }
      try {
        if (held.pc && held.pc.signalingState !== 'closed') held.pc.close();
      } catch (_) { /* best effort */ }
    }
    heldCallRef.current = null;
    setHasHeldCall(false);
    setHeldCallName('');
    setIsHeldByPeer(false);
    setWaitingCallSafe(null);
    
    // Reset all state
    setLocalStream(null);
    setRemoteStream(null);
    setPeerConnectionSafe(null);
    setIsCallActive(false);
    setIsIncomingCall(false);
    setCallStatus('ended');
    setIsMuted(false);
    setCallDuration(0);
    setCallerId(null);
    setCallerName('');
    setRemoteName('');
    setError(null);
    peerIdRef.current = null;
    pendingIceCandidates.current = [];
    pendingOffer.current = null;
    callIdRef.current = null;
    autoAcceptCall.current = false;
    // Reset call-history bookkeeping for the next call.
    callDirectionRef.current = null;
    callConnectedRef.current = false;
    callConnectedAtRef.current = null;
    callEndReasonRef.current = null;
    multiCallInviteParticipantsRef.current.clear();
    localStreamRef.current = null;
    remoteStreamRef.current = null;
    callTargetRef.current = null;
    
    // Reset cleanup flag after a delay to allow new calls
    setTimeout(() => {
      cleanupDoneRef.current = false;
    }, 1000);
    
    console.log('✅ AudioCall: Cleanup complete');
  };

  const toggleMute = () => {
    if (localStreamRef.current) {
      const audioTrack = localStreamRef.current.getAudioTracks()[0];
      if (audioTrack) {
        audioTrack.enabled = !audioTrack.enabled;
        setIsMuted(!audioTrack.enabled);
      }
    }
  };

  const formatDuration = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  // ---- Multi-participant: create a mesh peer connection to another participant ----
  const createPeerConnectionWithParticipant = async (participantId, shouldSendOffer = null) => {
    const myId = user.id?.toString();
    const otherUserId = participantId?.toString();

    if (additionalPeerConnectionsRef.current.has(otherUserId)) {
      const existingPc = additionalPeerConnectionsRef.current.get(otherUserId);
      const state = existingPc?.connectionState;
      const sigState = existingPc?.signalingState;
      if (sigState !== 'closed' && state !== 'failed' && state !== 'closed') {
        console.log(`⚠️ Already have audio connection with ${otherUserId} (state=${state})`);
        return existingPc;
      }
      try { existingPc.close(); } catch (_) { /* noop */ }
      additionalPeerConnectionsRef.current.delete(otherUserId);
    }

    let localAudioStream = localStreamRef.current;
    const hasLive = localAudioStream
      && typeof localAudioStream.getTracks === 'function'
      && localAudioStream.getTracks().some((t) => t.readyState === 'live');
    if (!hasLive) {
      try {
        localAudioStream = await ensureLocalAudioStream();
      } catch (err) {
        console.error('❌ Cannot acquire mic for new peer connection:', err);
        return null;
      }
    }

    try {
      const localStream = localAudioStream;
      const callId = callIdRef.current;

      // Tiebreaker: lower ID sends the offer to avoid glare.
      const isInitiator = shouldSendOffer !== null
        ? shouldSendOffer
        : (myId < otherUserId);

      console.log(`🔗 Creating audio peer connection with ${otherUserId} (${isInitiator ? 'INITIATOR' : 'RESPONDER'})`);

      const pc = new RTCPeerConnection({
        iceServers: config.iceServers,
        iceCandidatePoolSize: 10,
        bundlePolicy: 'max-bundle',
        rtcpMuxPolicy: 'require',
        iceTransportPolicy: config.forceRelay ? 'relay' : 'all',
      });
      pc._peerId = otherUserId;
      pc._callId = callId;

      additionalPeerConnectionsRef.current.set(otherUserId, pc);

      pc.onicecandidate = (event) => {
        if (event.candidate && socket) {
          socket.emit('multiAudioIceCandidate', {
            from: user.id,
            to: participantId,
            candidate: event.candidate,
            callId,
          });
        }
      };

      pc.ontrack = (event) => {
        console.log(`🎵 Multi-audio: track from ${participantId}`, { kind: event.track?.kind });
        let stream = remoteStreamsRef.current.get(otherUserId);
        if (event.streams && event.streams[0]) {
          stream = event.streams[0];
        } else if (event.track) {
          if (!stream) stream = new MediaStream();
          const existingTrack = stream.getTracks().find(t => t.id === event.track.id);
          if (!existingTrack) stream.addTrack(event.track);
        }
        if (stream) {
          upsertRemoteStream(otherUserId, stream);
          // Update primary remote audio if this peer is the original 1:1 callee/caller.
          const primaryId = (selectedUser?.id?.toString()) || (callerId?.toString()) || peerIdRef.current?.toString();
          if (otherUserId === primaryId) {
            setRemoteStream(stream);
            remoteStreamRef.current = stream;
            if (remoteAudioRef.current) {
              remoteAudioRef.current.srcObject = stream;
              remoteAudioRef.current.play().catch(() => {});
            }
          }
        }
      };

      pc.onconnectionstatechange = () => {
        console.log(`🔗 Multi-audio PC state (${otherUserId}):`, pc.connectionState);
      };

      if (isInitiator) {
        // Initiator: add tracks first, then create and send offer
        localStream.getTracks().forEach((track) => {
          pc.addTrack(track, localStream);
        });

        const offer = await pc.createOffer({
          offerToReceiveAudio: true,
          offerToReceiveVideo: false,
          voiceActivityDetection: true,
        });
        await pc.setLocalDescription(offer);

        socket.emit('multiAudioCallOffer', {
          from: myId,
          to: otherUserId,
          offer,
          callId,
          resume: sendingResumeOffersRef.current,
        });
      }
      // Responder: do NOT add tracks here. The caller will add tracks
      // after setRemoteDescription (correct WebRTC order for responders).

      return pc;
    } catch (error) {
      console.error(`❌ Error creating audio peer connection with ${otherUserId}:`, error);
      additionalPeerConnectionsRef.current.delete(otherUserId);
      throw error;
    }
  };

  // ---- Multi-participant: accept an incoming multi-audio invite ----
  const handleMultiAudioCallOffer = async (offer, fromId, callId, existingParticipantsFromInvite = []) => {
    console.log('📞 RECEIVER: handleMultiAudioCallOffer START', { fromId, callId });

    try {
      setError(null);
      cleanupDoneRef.current = false;

      // Get audio media (reuse if already live).
      let stream = localStreamRef.current;
      const hasLive = stream && stream.getTracks().some(t => t.readyState === 'live');
      if (!hasLive) {
        stream = await navigator.mediaDevices.getUserMedia({
          video: false,
          audio: {
            echoCancellation: true,
            noiseSuppression: true,
            autoGainControl: true,
            sampleRate: 48000,
            channelCount: 1,
          },
        });
        setLocalStream(stream);
        localStreamRef.current = stream;
      }

      const pc = new RTCPeerConnection({
        iceServers: config.iceServers,
        iceCandidatePoolSize: 10,
        bundlePolicy: 'max-bundle',
        rtcpMuxPolicy: 'require',
        iceTransportPolicy: config.forceRelay ? 'relay' : 'all',
      });
      pc._peerId = fromId;
      pc._callId = callId;
      setPeerConnectionSafe(pc);
      peerIdRef.current = fromId;

      pc.onicecandidate = (event) => {
        if (event.candidate && socket) {
          socket.emit('multiAudioIceCandidate', {
            from: user.id,
            to: fromId,
            candidate: event.candidate,
            callId,
          });
        }
      };

      pc.ontrack = (event) => {
        if (peerConnectionRef.current && peerConnectionRef.current !== pc) {
          if (fromId) upsertRemoteStream(fromId, event.streams?.[0] || new MediaStream([event.track]));
          return;
        }
        let s = remoteStreamRef.current;
        if (event.streams && event.streams[0]) {
          s = event.streams[0];
        } else if (event.track) {
          if (!s) s = new MediaStream();
          const existingTrack = s.getTracks().find(t => t.id === event.track.id);
          if (!existingTrack) s.addTrack(event.track);
        } else {
          return;
        }
        setRemoteStream(s);
        remoteStreamRef.current = s;
        upsertRemoteStream(fromId, s);
      };

      pc.onconnectionstatechange = () => {
        console.log('🔌 Multi-audio (receiver) PC state:', pc.connectionState);
        if (peerConnectionRef.current !== pc) return;
        if (pc.connectionState === 'failed' || pc.connectionState === 'disconnected') {
          scheduleCallRecovery(pc, {
            restartIce: pc.connectionState === 'failed',
            quiet: pc.connectionState === 'disconnected',
          });
        } else if (pc.connectionState === 'closed') {
          setError('Call connection failed');
          endCallRef.current?.();
        } else if (pc.connectionState === 'connected') {
          console.log('✅ Multi-audio (receiver) connected');
          setError(null);
          if (reconnectTimerRef.current) {
            clearTimeout(reconnectTimerRef.current);
            reconnectTimerRef.current = null;
          }
        }
      };

      await pc.setRemoteDescription(new RTCSessionDescription(offer));
      stream.getTracks().forEach((track) => pc.addTrack(track, stream));

      const answer = await pc.createAnswer();
      await pc.setLocalDescription(answer);

      socket.emit('multiAudioCallAnswer', {
        answer,
        from: user.id,
        to: fromId,
        callId,
      });

      // Build full participant list including ourselves and the inviter.
      const allExisting = (Array.isArray(existingParticipantsFromInvite) && existingParticipantsFromInvite.length > 0)
        ? existingParticipantsFromInvite.map(p => p?.toString()).filter(Boolean)
        : Array.from(allParticipants);
      const myId = user.id?.toString();
      const inviterId = fromId?.toString();
      const currentParticipants = [...allExisting];
      if (!currentParticipants.includes(myId)) currentParticipants.push(myId);
      if (!currentParticipants.includes(inviterId)) currentParticipants.push(inviterId);
      setAllParticipants(new Set(currentParticipants));

      socket.emit('multiAudioCallAccepted', {
        from: user.id,
        to: fromId,
        callId,
        existingParticipants: currentParticipants,
      });

      setRemoteName((prev) => prev || callerName || remoteName || 'Unknown User');
      setCallStatus('connected');
      setIsCallActive(true);
      isMultiParticipantCallRef.current = true;
      if (durationTimerRef.current === null) startCallTimer();

      // Connect to all OTHER participants we don't already have a link to.
      const otherParticipants = currentParticipants.filter(p => p && p !== myId && p !== inviterId);
      for (const pid of otherParticipants) {
        try {
          if (!additionalPeerConnectionsRef.current.has(pid)) {
            await createPeerConnectionWithParticipant(pid);
          }
        } catch (err) {
          console.error(`❌ Failed to mesh-connect to audio participant ${pid}:`, err);
        }
      }

      console.log('🎉 RECEIVER: handleMultiAudioCallOffer COMPLETE');
    } catch (error) {
      console.error('❌ RECEIVER: handleMultiAudioCallOffer FAILED', error);
      setError('Call failed: ' + error.message);
      if (socket && fromId) {
        socket.emit('multiAudioCallRejected', { from: user.id, to: fromId, callId });
      }
      setTimeout(() => handleEndCall(), 2000);
    }
  };

  // ---- Contacts modal: fetch contacts available to invite ----
  const fetchContacts = async () => {
    try {
      const response = await axios.get(`${config.apiUrl}/api/contacts/${user.id}`);
      if (!response.data || !Array.isArray(response.data)) {
        setContacts([]);
        setError('No contacts found');
        return;
      }

      const normalizedContacts = response.data.map((contact) => {
        const contactId = contact.id || contact._id?.toString() || contact.user?._id?.toString() || contact.user?.id?.toString();
        const username = contact.username || contact.user?.username || contact.recipient?.username || contact.requester?.username;
        const profileImage = contact.profileImage || contact.user?.profileImage || contact.recipient?.profileImage || contact.requester?.profileImage;
        return {
          id: contactId,
          username: username || contactId,
          profileImage,
          _id: contactId,
        };
      }).filter((c) => !!c.id);

      const myId = user.id?.toString();
      const filtered = normalizedContacts.filter((c) => c.id?.toString() !== myId);
      setContacts(filtered);
      if (filtered.length === 0) {
        setError('No contacts available to invite');
      } else {
        setError(null);
      }
    } catch (err) {
      console.error('❌ Audio: error fetching contacts:', err);
      setError('Failed to load contacts. Please try again.');
      setContacts([]);
    }
  };

  const handleAddParticipants = (e) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    setShowAddParticipants(true);
    setSelectedContacts(new Set());
    setParticipantSearchQuery('');
    setError(null);
    fetchContacts();
  };

  // Make sure we have a usable local audio stream before adding participants.
  // The ref/state can briefly be out of sync (e.g. between `connecting` and `connected`)
  // or the mic stream may have been muted/stopped by a hold/swap, so re-acquire if needed.
  const ensureLocalAudioStream = async () => {
    let stream = localStreamRef.current || localStream;
    const hasLive = stream && typeof stream.getTracks === 'function'
      && stream.getTracks().some((t) => t.readyState === 'live');
    if (hasLive) {
      localStreamRef.current = stream;
      return stream;
    }
    stream = await navigator.mediaDevices.getUserMedia({
      video: false,
      audio: {
        echoCancellation: true,
        noiseSuppression: true,
        autoGainControl: true,
        sampleRate: 48000,
        channelCount: 1,
      },
    });
    setLocalStream(stream);
    localStreamRef.current = stream;
    return stream;
  };

  const getCurrentParticipantCount = () => {
    const ids = new Set();
    const myId = user.id?.toString();
    if (myId) ids.add(myId);
    const otherPeer = selectedUser?.id?.toString() || callerId?.toString();
    if (otherPeer) ids.add(otherPeer);
    allParticipants.forEach((id) => { if (id) ids.add(id.toString()); });
    additionalPeerConnectionsRef.current.forEach((_pc, id) => { if (id) ids.add(id.toString()); });
    return ids.size;
  };

  const handleInviteParticipants = async () => {
    if (selectedContacts.size === 0) {
      setError('Please select at least one contact to invite');
      return;
    }
    if (!socket) {
      setError('Connection unavailable. Please try again in a moment.');
      return;
    }

    const currentCount = getCurrentParticipantCount();
    if (currentCount + selectedContacts.size > MAX_CALL_PARTICIPANTS) {
      const slotsLeft = Math.max(0, MAX_CALL_PARTICIPANTS - currentCount);
      setError(`Conference call limit is ${MAX_CALL_PARTICIPANTS} members. You can add ${slotsLeft} more participant${slotsLeft === 1 ? '' : 's'}.`);
      return;
    }

    try {
      try {
        await ensureLocalAudioStream();
      } catch (mediaErr) {
        console.error('❌ Could not acquire microphone for invite:', mediaErr);
        setError(
          mediaErr?.name === 'NotAllowedError'
            ? 'Microphone access denied. Please allow microphone permission.'
            : 'Microphone is unavailable. Please check your device.'
        );
        return;
      }

      const callId = callIdRef.current || generateCallId();
      callIdRef.current = callId;

      const myId = user.id?.toString();
      const otherParticipant = selectedUser?.id?.toString() || callerId?.toString();
      const currentParticipantsList = Array.from(allParticipants);
      if (myId && !currentParticipantsList.includes(myId)) currentParticipantsList.push(myId);
      if (otherParticipant && !currentParticipantsList.includes(otherParticipant)) currentParticipantsList.push(otherParticipant);
      additionalPeerConnectionsRef.current.forEach((_pc, participantId) => {
        if (participantId && !currentParticipantsList.includes(participantId)) {
          currentParticipantsList.push(participantId);
        }
      });
      const existingParticipants = currentParticipantsList.filter(Boolean);

      const callerDisplayName = user.username || user.name || 'Someone';

      // Seed the original 1:1 peer into the conference chip list so they appear
      // alongside the newly invited participants.
      const originalPeerId = (selectedUser?.id?.toString()) || (callerId?.toString());
      if (originalPeerId && remoteStreamRef.current) {
        upsertRemoteStream(originalPeerId, remoteStreamRef.current);
      }
      isMultiParticipantCallRef.current = true;

      // Seed allParticipants with the complete list including self and original peer
      setAllParticipants(new Set(existingParticipants));

      const invitePromises = Array.from(selectedContacts).map(async (contactId) => {
        try {
          const pc = await createPeerConnectionWithParticipant(contactId, true);
          if (!pc) throw new Error('Failed to create peer connection');
          await new Promise((resolve) => setTimeout(resolve, 100));
          const offer = pc.localDescription;
          if (!offer) throw new Error('No offer was created');

          socket.emit('multiAudioCallInvite', {
            from: user.id,
            to: contactId,
            offer,
            callId,
            existingParticipants,
            callerName: callerDisplayName,
          });

          setAllParticipants((prev) => {
            const next = new Set(prev);
            next.add(contactId);
            return next;
          });
          isMultiParticipantCallRef.current = true;
          return { success: true, contactId };
        } catch (err) {
          console.error(`❌ Audio invite failed for ${contactId}:`, err);
          return { success: false, contactId, error: err.message };
        }
      });

      const results = await Promise.all(invitePromises);
      const successCount = results.filter((r) => r.success).length;
      if (successCount > 0) {
        setShowAddParticipants(false);
        setSelectedContacts(new Set());
        setError(null);
      } else {
        setError('Failed to invite participants. Please try again.');
      }
    } catch (error) {
      console.error('❌ Error inviting audio participants:', error);
      setError('Failed to invite participants: ' + (error.message || 'Unknown error'));
    }
  };

  // Resolve usernames for tiles in the connected UI.
  useEffect(() => {
    let cancelled = false;
    const myId = user.id?.toString();
    const ids = Array.from(allParticipants).filter(id => id && id !== myId);
    // Also include IDs from streams in case allParticipants is behind
    remoteParticipantStreams.forEach((p) => {
      const uid = p.userId?.toString?.() || String(p.userId || '');
      if (uid && !ids.includes(uid)) ids.push(uid);
    });
    ids.forEach(async (uid) => {
      if (participantNames[uid]) return;
      const name = await fetchUserName(uid);
      if (!cancelled) {
        setParticipantNames((prev) => (prev[uid] ? prev : { ...prev, [uid]: name }));
      }
    });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allParticipants, remoteParticipantStreams]);

  // Attach local stream to audio element
  useEffect(() => {
    if (localAudioRef.current && localStreamRef.current) {
      localAudioRef.current.srcObject = localStreamRef.current;
      localAudioRef.current.muted = true;
      localAudioRef.current.play().catch(err => {
        console.warn('Local audio play error:', err);
      });
    } else if (localAudioRef.current && !localStreamRef.current) {
      localAudioRef.current.srcObject = null;
    }
  }, [localStream]);

  // Attach remote stream to audio element and ensure playback
  useEffect(() => {
    if (!remoteAudioRef.current || !remoteStreamRef.current) {
      return;
    }

    const audioElement = remoteAudioRef.current;
    const stream = remoteStreamRef.current;
    
    // Set up audio element
    audioElement.srcObject = stream;
    audioElement.muted = false;
    audioElement.volume = 1.0;
    audioElement.autoplay = true;
    audioElement.playsInline = true;
    
    // Ensure playback
    const playAudio = async () => {
      try {
        await audioElement.play();
        console.log('✅ AudioCall: Remote audio playback started successfully!');
      } catch (err) {
        console.warn('⚠️ AudioCall: Audio playback failed:', err.message);
        // Retry once after a short delay
        setTimeout(async () => {
          try {
            await audioElement.play();
            console.log('✅ AudioCall: Remote audio playback started after retry!');
          } catch (retryErr) {
            console.error('❌ AudioCall: Audio playback failed after retry:', retryErr.message);
          }
        }, 500);
      }
    };
    
    // Wait for metadata to load, then play
    audioElement.onloadedmetadata = () => {
      playAudio();
    };
    
    // Also try to play immediately if already loaded
    if (audioElement.readyState >= 2) {
      playAudio();
    }
  }, [remoteStream]);

  // End this audio call if a video or group call is accepted.
  useEffect(() => {
    const onOtherCallAccepted = (e) => {
      const accepted = e.detail?.acceptedType;
      if (
        (accepted === 'video' || accepted === 'group') &&
        callStatusRef.current !== 'idle' &&
        callStatusRef.current !== 'ended'
      ) {
        console.log('🔄 AudioCall: ending because another call was accepted:', accepted);
        endCall();
      }
    };
    window.addEventListener('endOtherCallTypes', onOtherCallAccepted);
    return () => window.removeEventListener('endOtherCallTypes', onOtherCallAccepted);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    return registerCallSession({
      id: sessionIdRef.current,
      kind: 'direct',
      callType: 'audio',
      isActive: () => {
        const s = callStatusRef.current;
        return s === 'incoming' || s === 'calling' || s === 'connecting' || s === 'connected';
      },
      leave: () => endCallRef.current?.(),
    });
  }, []);

  // Listen for outgoing-call requests dispatched by the chat-header button
  // (Home.js). We do this so the AudioCall component is mounted exactly once
  // per app (in App.js); the chat-header just publishes "please call this user"
  // and the global instance handles the entire call lifecycle. This prevents
  // duplicate socket listeners from showing the incoming-call modal twice.
  useEffect(() => {
    const onRequestStart = (e) => {
      const target = e.detail?.user;
      if (!target) return;
      if (callStatusRef.current !== 'idle' && callStatusRef.current !== 'ended') {
        console.warn('⚠️ AudioCall: ignoring requestStartAudioCall — already in call', callStatusRef.current);
        return;
      }
      startCall(target);
    };
    window.addEventListener('requestStartAudioCall', onRequestStart);
    return () => window.removeEventListener('requestStartAudioCall', onRequestStart);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const buildAudioSnapshot = () => {
    const status = callStatusRef.current;
    if (!['calling', 'connecting', 'connected'].includes(status)) return null;
    const peerId = (callTargetRef.current?.id || peerIdRef.current || callerId)?.toString();
    if (!peerId) return null;
    if (!callIdRef.current) callIdRef.current = generateCallId();
    return {
      kind: 'audio',
      userId: user?.id?.toString(),
      callId: callIdRef.current,
      status,
      peer: {
        id: peerId,
        username: callTargetRef.current?.username || remoteName || callerName || '',
      },
      participants: Array.from(allParticipantsRef.current || []),
      isMulti: !!isMultiParticipantCallRef.current,
      direction: callDirectionRef.current,
      connectedAt: callConnectedAtRef.current,
      isMuted,
    };
  };

  useEffect(() => {
    allParticipantsRef.current = allParticipants;
  }, [allParticipants]);

  useEffect(() => {
    const active = ['calling', 'connecting', 'connected'].includes(callStatus);
    if (active) {
      hadLiveCallRef.current = true;
      const snapshot = buildAudioSnapshot();
      if (snapshot) saveActiveCall(snapshot);
      return;
    }
    if (hadLiveCallRef.current && !isPageUnloading()) {
      hadLiveCallRef.current = false;
      clearActiveCall('audio');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [callStatus, isMuted, remoteName, callerName, allParticipants]);

  buildAudioSnapshotRef.current = buildAudioSnapshot;

  useEffect(() => {
    const flush = () => {
      const snapshot = buildAudioSnapshotRef.current?.();
      if (snapshot) saveActiveCall(snapshot);
    };
    window.addEventListener('beforeunload', flush);
    window.addEventListener('pagehide', flush);
    return () => {
      window.removeEventListener('beforeunload', flush);
      window.removeEventListener('pagehide', flush);
    };
  }, []);

  useEffect(() => {
    if (!socket || !user?.id || resumeAttemptedRef.current) return undefined;
    const preview = readActiveCall();
    if (!preview || preview.kind !== 'audio') return undefined;
    if (preview.userId && preview.userId !== user.id.toString()) return undefined;
    resumeAttemptedRef.current = true;
    const snapshot = consumeActiveCall('audio', user.id);
    if (!snapshot?.peer?.id) return undefined;

    let cancelled = false;
    (async () => {
      const ready = await waitForSocket(socket);
      if (cancelled || !ready) {
        if (!cancelled && !ready) setError('Could not reconnect to the call');
        return;
      }
      callIdRef.current = snapshot.callId || callIdRef.current;
      callDirectionRef.current = snapshot.direction || 'outgoing';
      peerIdRef.current = snapshot.peer.id;
      callTargetRef.current = snapshot.peer;
      setRemoteName(snapshot.peer.username || '');
      setCallerName(snapshot.peer.username || '');
      if (snapshot.connectedAt) {
        callConnectedRef.current = true;
        callConnectedAtRef.current = snapshot.connectedAt;
      }
      const others = (snapshot.participants || [])
        .map((id) => id?.toString())
        .filter((id) => id && id !== user.id.toString());
      const resumeMulti = !!snapshot.isMulti && others.length > 0;
      if (!resumeMulti) {
        await startCall(snapshot.peer, {
          resume: !!snapshot.connectedAt,
          wasConnected: !!snapshot.connectedAt,
          callId: snapshot.callId,
          isMuted: snapshot.isMuted,
        });
        return;
      }

      isMultiParticipantCallRef.current = true;
      setAllParticipants(new Set([user.id.toString(), snapshot.peer.id.toString(), ...others]));
      setIsCallActive(true);
      setCallStatus(snapshot.connectedAt ? 'connecting' : 'calling');
      setError('Reconnecting…');
      try {
        const stream = await navigator.mediaDevices.getUserMedia({
          video: false,
          audio: {
            echoCancellation: true,
            noiseSuppression: true,
            autoGainControl: true,
          },
        });
        if (snapshot.isMuted) stream.getAudioTracks().forEach((track) => { track.enabled = false; });
        setIsMuted(!!snapshot.isMuted);
        setLocalStream(stream);
        localStreamRef.current = stream;

        const rejoin = await new Promise((resolve) => {
          socket.emit('multiAudioCallRejoin', {
            from: user.id,
            callId: snapshot.callId,
            existingParticipants: [user.id.toString(), snapshot.peer.id.toString(), ...others],
          }, (ack) => resolve(ack || { ok: false }));
        });
        if (!rejoin?.ok) {
          setError('The call has ended');
          handleEndCall();
          return;
        }
        const roster = (rejoin.participants || others).map((id) => id?.toString()).filter(Boolean);
        sendingResumeOffersRef.current = true;
        try {
          for (const pid of roster) {
            if (!pid || pid === user.id.toString()) continue;
            await createPeerConnectionWithParticipant(pid, true);
          }
        } finally {
          sendingResumeOffersRef.current = false;
        }
        if (snapshot.connectedAt) startCallTimer();
        setError(null);
      } catch (err) {
        console.error('Failed to rejoin audio call:', err);
        setError('Could not reconnect to the call');
        handleEndCall();
      }
    })();

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [socket, user?.id]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      handleEndCall();
    };
  }, []);

  // Incoming call modal
  if (isIncomingCall) {
    return createPortal(
      (<div className="video-call-overlay">
        <div className="video-call-modal incoming">
          <audio ref={remoteAudioRef} autoPlay playsInline muted={false} controls={false} style={{ display: 'none' }} />
          <audio ref={localAudioRef} autoPlay playsInline muted controls={false} style={{ display: 'none' }} />
          <div className="call-header">
            <h3>Incoming Audio Call</h3>
            <p>{callerName || callerId || 'Unknown'}</p>
          </div>
          {error && <div className="call-error">{error}</div>}
          <div className="call-actions">
            <button className="accept-btn" onClick={acceptCall}>
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M22 16.92v3a2 2 0 01-2.18 2 19.79 19.79 0 01-8.63-3.07 19.5 19.5 0 01-6-6 19.79 19.79 0 01-3.07-8.67A2 2 0 014.11 2h3a2 2 0 012 1.72 12.84 12.84 0 00.7 2.81 2 2 0 01-.45 2.11L8.09 9.91a16 16 0 006 6l1.27-1.27a2 2 0 012.11-.45 12.84 12.84 0 002.81.7A2 2 0 0122 16.92z"/>
              </svg>
              Accept
            </button>
            <button className="reject-btn" onClick={rejectCall}>
              <EndCallIcon size={24} />
              Reject
            </button>
          </div>
        </div>
      </div>),
      document.body
    );
  }

  // Calling overlay (for caller)
  if (callStatus === 'calling') {
    return createPortal(
      (<div className="video-call-overlay">
        <div className="video-call-modal calling">
          <audio ref={remoteAudioRef} autoPlay playsInline muted={false} controls={false} style={{ display: 'none' }} />
          <audio ref={localAudioRef} autoPlay playsInline muted controls={false} style={{ display: 'none' }} />
          <div className="call-header">
            <h3>Calling...</h3>
            <p>{remoteName || selectedUser?.username || 'Unknown'}</p>
          </div>
          {error && <div className="call-error">{error}</div>}
          <div className="call-status">
            <div className="calling-animation">
              <div className="pulse"></div>
              <div className="pulse"></div>
              <div className="pulse"></div>
            </div>
          </div>
          <div className="call-actions">
            <button className="end-call-btn" onClick={endCall}>
              <EndCallIcon size={24} />
              End Call
            </button>
          </div>
        </div>
      </div>),
      document.body
    );
  }

  // Connected call UI
  if (callStatus === 'connected' || callStatus === 'connecting') {
    const isFullyConnected = callStatus === 'connected';
    const isConference = isMultiParticipantCallRef.current || remoteParticipantStreams.length > 0;
    // allParticipants includes self, so use its size directly; fall back to streams + 1
    const participantCount = allParticipants.size > 1
      ? allParticipants.size
      : 1 + Math.max(remoteParticipantStreams.length, 0);
    return (
      <>
        {createPortal(
      (<div className="video-call-overlay">
        <div className="video-call-container">
          <audio 
            ref={remoteAudioRef} 
            autoPlay={true}
            playsInline={true}
            muted={false}
            controls={false}
            style={{ display: 'none' }}
          />
          <audio 
            ref={localAudioRef} 
            autoPlay={true}
            playsInline={true}
            muted={true}
            controls={false}
            style={{ display: 'none' }}
          />

          {isConference && (
            <div className="audio-conf-participants" aria-label="Call participants">
              <div className="audio-conf-participant audio-conf-participant--me">
                <div className="audio-conf-avatar">
                  {(user?.username || 'You').charAt(0).toUpperCase()}
                </div>
                <span className="audio-conf-name">{user?.username || 'You'} (You)</span>
              </div>
              {Array.from(allParticipants)
                .filter(id => id && id !== user.id?.toString())
                .map(userId => (
                <div key={userId} className="audio-conf-participant">
                  <div className="audio-conf-avatar">
                    {(participantNames[userId] || userId || '?').charAt(0).toUpperCase()}
                  </div>
                  <span className="audio-conf-name">{participantNames[userId] || userId}</span>
                </div>
              ))}
            </div>
          )}

          <div className="video-call-controls">
            <button className={`control-btn ${isMuted ? 'active' : ''}`} onClick={toggleMute} title={isMuted ? 'Unmute' : 'Mute'}>
              <MuteIcon muted={isMuted} size={20} />
            </button>

            {/* Add participants button hidden for individual calls
            <button
              className="control-btn"
              type="button"
              onClick={(e) => { e.preventDefault(); e.stopPropagation(); handleAddParticipants(); }}
              title="Add participants"
              aria-label="Add participants"
            >
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M16 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/>
                <circle cx="8.5" cy="7" r="4"/>
                <line x1="20" y1="8" x2="20" y2="14"/>
                <line x1="23" y1="11" x2="17" y2="11"/>
              </svg>
            </button>
            */}

            <button className="control-btn end-call" onClick={endCall} title="End call">
              <EndCallIcon size={20} />
            </button>
           </div>

          <div className="call-info">
            <h3>{isConference ? 'Audio conference' : (remoteName || selectedUser?.username || 'Unknown')}</h3>
            {isConference && (
              <p className="participants-count">
                {participantCount} participant{participantCount === 1 ? '' : 's'}
              </p>
            )}
            <span className="call-duration">
              {isFullyConnected ? formatDuration(callDuration) : 'Connecting...'}
            </span>
          </div>
        </div>
      </div>),
      document.body
    )}

        {showAddParticipants && createPortal(
          <div
            className="modal-overlay"
            style={{ display: 'flex' }}
            onClick={() => {
              setShowAddParticipants(false);
              setParticipantSearchQuery('');
            }}
          >
            <div className="modal-content" onClick={(e) => e.stopPropagation()}>
              <div className="modal-header">
                <h2>Add Participants</h2>
                <button
                  className="close-btn"
                  type="button"
                  onClick={() => {
                    setShowAddParticipants(false);
                    setParticipantSearchQuery('');
                  }}
                >
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <line x1="18" y1="6" x2="6" y2="18"/>
                    <line x1="6" y1="6" x2="18" y2="18"/>
                  </svg>
                </button>
              </div>
              <div className="modal-body">
                <div className="contacts-search-wrapper">
                  <svg className="contacts-search-icon" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <circle cx="11" cy="11" r="7"/>
                    <line x1="21" y1="21" x2="16.65" y2="16.65"/>
                  </svg>
                  <input
                    type="text"
                    className="contacts-search-input"
                    placeholder="Search contacts..."
                    value={participantSearchQuery}
                    onChange={(e) => setParticipantSearchQuery(e.target.value)}
                    autoFocus
                  />
                  {participantSearchQuery && (
                    <button
                      type="button"
                      className="contacts-search-clear"
                      onClick={() => setParticipantSearchQuery('')}
                      aria-label="Clear search"
                    >
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <line x1="18" y1="6" x2="6" y2="18"/>
                        <line x1="6" y1="6" x2="18" y2="18"/>
                      </svg>
                    </button>
                  )}
                </div>
                {error && (
                  <div className="call-error" style={{ marginBottom: '16px' }}>{error}</div>
                )}
                {(() => {
                  const inCallIds = new Set();
                  const otherPeer = selectedUser?.id?.toString() || callerId?.toString();
                  if (otherPeer) inCallIds.add(otherPeer);
                  allParticipants.forEach((id) => { if (id) inCallIds.add(id.toString()); });
                  additionalPeerConnectionsRef.current.forEach((_pc, id) => {
                    if (id) inCallIds.add(id.toString());
                  });
                  const myId = user.id?.toString();
                  if (myId) inCallIds.delete(myId);

                  const currentCount = inCallIds.size + 1; // +1 for self
                  const slotsLeft = Math.max(0, MAX_CALL_PARTICIPANTS - currentCount);
                  const selectionLimitReached = selectedContacts.size >= slotsLeft;

                  const query = participantSearchQuery.trim().toLowerCase();
                  const visibleContacts = contacts.filter((contact) => {
                    if (!query) return true;
                    return (contact.username || '').toLowerCase().includes(query);
                  });

                  if (contacts.length === 0) {
                    return (
                      <p style={{ textAlign: 'center', padding: '20px', color: '#54656f' }}>
                        {error ? error : 'No contacts available to invite'}
                      </p>
                    );
                  }
                  if (visibleContacts.length === 0) {
                    return (
                      <p style={{ textAlign: 'center', padding: '20px', color: '#54656f' }}>
                        No contacts match &ldquo;{participantSearchQuery}&rdquo;
                      </p>
                    );
                  }

                  return (
                    <>
                      {selectionLimitReached && slotsLeft > 0 && (
                        <p style={{ textAlign: 'center', padding: '8px 12px', color: '#e65100', fontSize: '13px', background: '#fff3e0', borderRadius: '8px', margin: '0 0 8px' }}>
                          Conference call limit is {MAX_CALL_PARTICIPANTS} members. You can add {slotsLeft} more.
                        </p>
                      )}
                      {slotsLeft === 0 && (
                        <p style={{ textAlign: 'center', padding: '8px 12px', color: '#c62828', fontSize: '13px', background: '#ffebee', borderRadius: '8px', margin: '0 0 8px' }}>
                          Conference call limit reached ({MAX_CALL_PARTICIPANTS} members). Cannot add more participants.
                        </p>
                      )}
                    <div className="contacts-list">
                      {visibleContacts.map((contact) => {
                        const contactId = contact.id || contact._id?.toString();
                        if (!contactId) return null;
                        const username = contact.username || contactId;
                        const profileImage = contact.profileImage;
                        const alreadyInCall = inCallIds.has(contactId.toString());
                        const isSelected = !alreadyInCall && selectedContacts.has(contactId);
                        const isDisabledByLimit = !alreadyInCall && !isSelected && (selectionLimitReached || slotsLeft === 0);

                        return (
                          <div
                            key={contactId}
                            className={`contact-item ${isSelected ? 'selected' : ''} ${alreadyInCall ? 'in-call' : ''} ${isDisabledByLimit ? 'disabled' : ''}`}
                            aria-disabled={alreadyInCall || isDisabledByLimit}
                            onClick={() => {
                              if (alreadyInCall || isDisabledByLimit) return;
                              const next = new Set(selectedContacts);
                              if (isSelected) next.delete(contactId);
                              else next.add(contactId);
                              setSelectedContacts(next);
                            }}
                          >
                            <div className="contact-avatar">
                              {profileImage ? (
                                <img
                                  src={`${config.apiUrl}${profileImage}`}
                                  alt={username}
                                  onError={(e) => {
                                    e.target.style.display = 'none';
                                    if (e.target.nextSibling) e.target.nextSibling.style.display = 'flex';
                                  }}
                                />
                              ) : null}
                              <div className="avatar-placeholder" style={{ display: profileImage ? 'none' : 'flex' }}>
                                {username?.charAt(0).toUpperCase() || '?'}
                              </div>
                            </div>
                            <div className="contact-info">
                              <span className="contact-name">{username}</span>
                              {alreadyInCall && <span className="contact-substatus">In this call</span>}
                            </div>
                            {alreadyInCall ? (
                              <div className="in-call-badge" title="This contact is already in the call">Already in call</div>
                            ) : isSelected ? (
                              <div className="checkmark">✓</div>
                            ) : null}
                          </div>
                        );
                      })}
                    </div>
                    </>
                  );
                })()}
              </div>
              <div className="modal-footer">
                <button
                  className="btn-secondary"
                  type="button"
                  onClick={() => {
                    setShowAddParticipants(false);
                    setSelectedContacts(new Set());
                    setParticipantSearchQuery('');
                  }}
                >
                  Cancel
                </button>
                <button
                  className="btn-primary"
                  type="button"
                  onClick={handleInviteParticipants}
                  disabled={selectedContacts.size === 0}
                >
                  Invite {selectedContacts.size > 0 ? `(${selectedContacts.size})` : ''}
                </button>
              </div>
            </div>
          </div>,
          document.body
        )}
        {waitingCall && createPortal(
          <div className="call-waiting-banner" role="alertdialog" aria-label="Incoming call while busy">
            <div className="call-waiting-info">
              <div className="call-waiting-title">Incoming audio call</div>
              <div className="call-waiting-name">{waitingCall.fromName || waitingCall.from}</div>
            </div>
            <div className="call-waiting-actions">
              <button type="button" className="accept-btn" onClick={acceptWaitingCall} title="Hold current call and answer">Accept</button>
              <button type="button" className="reject-btn" onClick={rejectWaitingCall} title="Decline">Reject</button>
            </div>
          </div>,
          document.body
        )}
        {hasHeldCall && createPortal(
          <div className="call-on-hold-chip" role="status" aria-label="Call on hold">
            <span className="call-on-hold-dot" aria-hidden="true" />
            <span className="call-on-hold-text">
              <span className="call-on-hold-label">On hold</span>
              <span className="call-on-hold-name">{heldCallName || 'Other call'}</span>
            </span>
            <button type="button" className="call-on-hold-swap" onClick={swapHeldCall} title="Resume this call (current call goes on hold)">Swap</button>
            <button type="button" className="call-on-hold-end" onClick={endHeldCall} title="End the held call">End</button>
          </div>,
          document.body
        )}
        {isHeldByPeer && createPortal(
          <div className="call-held-by-peer-overlay" role="status" aria-label="You are on hold">
            <div className="call-held-by-peer-card">
              <div className="call-held-by-peer-title">On hold</div>
              <div className="call-held-by-peer-sub">{remoteName || callerName || 'The other party'} put you on hold</div>
            </div>
          </div>,
          document.body
        )}
      </>
    );
  }

  // When mounted globally without a selectedUser (App.js does this so the
  // component can listen for incoming calls anywhere in the app), don't render
  // a stray disabled button. The chat-header in Home.js renders its own
  // trigger button that dispatches `requestStartAudioCall`.
  if (!selectedUser) return null;

  // Default idle button in chat header: icon-only
  return (
    <span
      className={`action-icon${!selectedUser ? ' disabled' : ''}`}
      onClick={(e) => {
        if (selectedUser) startCall();
      }}
      title={selectedUser ? 'Start audio call' : 'Select a user to call'}
      role="button"
      aria-label="Start audio call"
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === 'Enter' && selectedUser) startCall();
      }}
    >
      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <path d="M22 16.92v3a2 2 0 01-2.18 2 19.79 19.79 0 01-8.63-3.07 19.5 19.5 0 01-6-6 19.79 19.79 0 01-3.07-8.67A2 2 0 014.11 2h3a2 2 0 012 1.72 12.84 12.84 0 00.7 2.81 2 2 0 01-.45 2.11L8.09 9.91a16 16 0 006 6l1.27-1.27a2 2 0 012.11-.45 12.84 12.84 0 002.81.7A2 2 0 0122 16.92z"/>
      </svg>
    </span>
  );
};

export default AudioCall;
